import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Animated,
  AppState,
  Dimensions,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  Vibration,
  View,
} from 'react-native';
import { Audio } from 'expo-av';
import { Check, Clock, Crosshair, MoreVertical, RotateCcw, X } from 'lucide-react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Svg, { Line } from 'react-native-svg';
import { supabase } from '../lib/supabase';
import { notifyDataChanged } from '../live/screenContext';
import { useAuth } from '../auth/AuthProvider';
import { LinearGradient } from 'expo-linear-gradient';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos } from '../v10/ui';
import { ScreenHelp } from '../v10/ScreenHelp';
import { metisHelp } from '../olmps/help/metisHelp';
import type { ToolProps } from '../olmps/ToolHost';
import { cancelTimerNotification, scheduleTimerNotification } from '../lib/notifications';
import { TrajectoryView } from '../olmps/TrajectoryView';
import { ProtocolDetailScreen } from './ThemisTool';
import { SECTORS, type Sector } from '../domain/olmps';
import { useOptionalSharedTodayData } from '../olmps/TodayDataContext';
import { loadOracleSpine, effectiveAims, SECTOR_DEITY_MAP, type OracleSpine } from '../olmps/oracle-spine-data';
import { atlasOf } from '../olmps/atlas-data';

// THEMIS protocol picker data (subset of ThemisTool's rows) — loaded in METIS
// so an agenda item's THEMIS tab can browse categories → protocols.
interface ProtocolPickRow {
  id: string;
  title: string;
  group_id: string | null;
  order: number | null;
}
interface ProtocolGroupPick {
  id: string;
  name: string;
  order: number | null;
}

// ─────────────────────────────────────────────────────────────────────────
// FULL-FIDELITY native METIS agenda, mirroring src/app/agenda/page.tsx +
// src/components/agenda/{AgendaTable,SortableRow,NailRow}.tsx (the web
// "golden reference"). Same tables/columns, same date-keyed persistence,
// same PROFILES / AGENDA / MONTHLY / WEEKLY sections. The only deliberate
// deviations from the web page are documented inline where they occur:
//   - Drag-and-drop reordering → explicit ▲▼ buttons (same `sort_order`
//     column / array order is written either way).
//   - No localStorage mirroring/beacon-on-unload — writes go straight to
//     Supabase (native has no reliable "page is dying" hook).
//   - The web page's dropdown-menu (⋮) per row becomes a native Alert
//     action sheet, matching the existing convention used elsewhere in this
//     app (see JnusTool's `showOptionMenu`).
//
// Audio + Pomodoro parity (closed 2026-07-14):
//   - Duration-timer audio now mirrors SortableRow.tsx's `useAudio()` Web
//     Audio hook: a countdown tick in the final 10s (`playBeep(660, 0.1)`)
//     and a completion chime (`playRinger()`, 880/1100/1320Hz ascending) —
//     reproduced here as embedded base64 WAV data-URIs played via expo-av,
//     since no shippable audio asset exists in apps/mobile/assets and the
//     web tones are synthesized in-browser, not files. Vibration is kept
//     alongside audio (native convention), not replaced.
//   - Pomodoro is a genuinely separate subsystem on web (PomodoroProvider
//     context, mounted app-wide, independent of the agenda page). This file
//     reimplements its work/break auto-cycling engine natively, scoped to
//     METIS: honors `pomodoro_work_time` / `pomodoro_break_time` /
//     `pomodoro_break_start_bell` / `pomodoro_break_end_bell` from
//     user_settings, computes remaining time from wall-clock timestamps
//     (not accumulated ticks) so brief backgrounding doesn't drift, and
//     plays a bell on every phase transition. Web's background-music
//     playlist engine (`pomodoroMusicFolder`/`pomodoroSelectedMusic`,
//     fetched from Supabase storage) has no mobile equivalent — no asset
//     pipeline exists for it here — so `pomodoro_repeat` ("Repeat Music
//     During Break" on web) is reinterpreted as "repeat the break-start
//     bell every 20s for the duration of the break," the closest audible
//     analog without a music engine. This is a deliberate, documented
//     deviation, not a silent gap.
//   - Backgrounded/killed-app alerts (closed 2026-07-14): every armed
//     deadline also schedules a local notification via
//     scheduleTimerNotification (src/lib/notifications.ts) for that same
//     wall-clock instant ("<item text> — time's up" / pomodoro work-end /
//     break-end copy). If the in-app expiry handler fires first (the app is
//     foregrounded), it cancels the pending notification before playing its
//     own audio/vibration cue, so the two never double up — see
//     notifications.ts's handler-config comment for why cancel-on-in-app-fire
//     was chosen over relying on OS foreground suppression. Stopping/pausing/
//     resetting a timer before it completes also cancels its notification.
// ─────────────────────────────────────────────────────────────────────────

// ── Embedded WAV tone data-URIs (no shippable audio asset exists in
// apps/mobile/assets, per task constraints). Each is a short synthesized
// tone matching the web SortableRow `useAudio()` hook's shape (single decaying
// oscillator beep for countdown ticks, multi-tone decaying chime for
// completion), rendered as 8kHz mono 16-bit PCM WAV, base64-embedded so no
// bundler asset registration is required.
//   - DURATION_TICK_WAV: single 660Hz/0.1s blip — countdown tick (mirrors
//     web's `playBeep(660, 0.1)` in the last 10s of a duration timer).
//   - WORK_END_WAV: descending two-tone (660Hz→523Hz) — duration timer /
//     pomodoro work-phase completion (distinct lower pitch = "stop").
//   - BREAK_END_WAV: ascending three-tone chime (880/1100/1320Hz) — mirrors
//     web's `playRinger()` exactly, used for pomodoro break-phase completion
//     (distinct higher/brighter pattern = "resume").
const DURATION_TICK_WAV = 'data:audio/wav;base64,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';
const WORK_END_WAV = 'data:audio/wav;base64,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';
const BREAK_END_WAV = 'data:audio/wav;base64,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';

// ── Native audio playback helper (expo-av), mirroring web's useAudio(). ──
async function playToneOnce(uri: string): Promise<void> {
  try {
    const { sound } = await Audio.Sound.createAsync({ uri }, { shouldPlay: true, volume: 1.0 });
    sound.setOnPlaybackStatusUpdate((status) => {
      if (status.isLoaded && status.didJustFinish) {
        sound.unloadAsync().catch(() => {});
      }
    });
  } catch {
    // Audio not available (e.g. simulator without audio session) — vibration
    // (always paired with these calls at the call site) still fires.
  }
}

let audioModeConfigured = false;
async function ensureAudioMode(): Promise<void> {
  if (audioModeConfigured) return;
  audioModeConfigured = true;
  try {
    await Audio.setAudioModeAsync({ playsInSilentModeIOS: true, staysActiveInBackground: false });
  } catch {
    // Non-fatal — playback still attempts via default audio session.
  }
}

function playDurationTick(): void {
  void ensureAudioMode().then(() => playToneOnce(DURATION_TICK_WAV));
}
function playWorkEndChime(): void {
  Vibration.vibrate(400);
  void ensureAudioMode().then(() => playToneOnce(WORK_END_WAV));
}
function playBreakEndChime(): void {
  Vibration.vibrate([0, 200, 100, 200]);
  void ensureAudioMode().then(() => playToneOnce(BREAK_END_WAV));
}

// Pomodoro bell settings, as read from user_settings (see PomodoroEditor.tsx
// for the same columns' write side, and settings-context.tsx on web for the
// canonical field names/defaults this mirrors).
interface PomodoroSettings {
  enabled: boolean;
  workSeconds: number;
  breakSeconds: number;
  breakStartBell: string; // 'bell-1' | 'bell-2' | 'bell-3' (web bell ids)
  breakEndBell: string;
  repeat: boolean; // web: "Repeat Music During Break" — see file header note
}

// Web ships three distinct bell-N.mp3 files with no fixed "which pitch"
// semantics beyond user choice; mobile has two distinct synthesized tones
// (a lower "settle down" chime and a brighter "wake up" chime). Every bell
// id maps to one of the two based on its role (isWorkEnd = transitioning
// OUT of work, i.e. the break-start bell) so the two phase transitions are
// always audibly distinct, matching the web UX intent even though the
// exact bell-1/2/3 timbre isn't reproduced (no shippable mp3 assets exist
// in apps/mobile/assets).
function playPomodoroBell(_bellId: string, isWorkEnd: boolean): void {
  if (isWorkEnd) playBreakEndChime(); // brighter ascending chime = "break begins"
  else playWorkEndChime(); // lower descending chime = "back to work"
}

import { subscribeAgendaRowsChanged } from '../olmps/links/kyrsToMetis';
import { useOnboarding, useTourTarget, tourBus } from '../v10/onboarding';
import { prefillAgendaFromKyrs } from './metis/prefillAgenda';

/** Tour target wrapper (Pressables/gesture handles take no tour ref). id '' = unregistered. */
function TourBox({ id, children }: { id: string; children: React.ReactNode }) {
  const tt = useTourTarget(id);
  return <View ref={tt.onLayoutRef} collapsable={false}>{children}</View>;
}
const AGENDA_TABLE = 'agenda_items';
const SETTINGS_TABLE = 'user_settings';

// ── interfaces/agenda.ts, replicated (no `@/interfaces/*` alias in RN) ──
interface NailItem {
  id: string;
  text: string;
  elapsed: number;
}

interface DesignRowData {
  label: string;
  value: string;
}

interface DesignData {
  rows: DesignRowData[];
  templateId?: string;
}

interface PastDesignEntry {
  date: string;
  text: string;
  design: DesignData;
}

interface AgendaItem {
  id: string;
  item_id: string;
  completed: boolean;
  text: string;
  time: string;
  duration: string;
  sort_order: number;
  nails: NailItem[];
  design: DesignData | null;
  missed_reason: string;
  note: string;
  // Cross-tool links (null/undefined for hand-typed rows).
  task_id?: string | null;
  sector?: string | null;
  design_id?: string | null;
  aim_ref?: string | null;
}

// ── interfaces/settings.ts, the subset the agenda page reads/writes ──
interface MetisDesignTemplate {
  id: string;
  name: string;
  rows: DesignRowData[];
}

interface AgendaProfile {
  id: string;
  label: string;
  itemsByDate?: Record<string, AgendaItem[]>;
  startTime?: string | null;
  endTime?: string | null;
}

interface MonthlyGoal {
  id: string;
  monthKey: string;
  text: string;
  completed: boolean;
  sortOrder: number;
  // ── Oracle auto-population (back-compat optional) ──
  // Present only on rows seeded from the Oracle spine ("goal:<sector>:<goalId>").
  oracleRef?: string;
  sector?: string;
  // Soft-delete for oracle-seeded rows — never hard-delete them or they'd
  // resurrect on the next seeding pass. See deleteMonthlyGoal.
  dismissed?: boolean;
}

interface WeeklyGoal {
  id: string;
  weekKey: string;
  text: string;
  completed: boolean;
  sortOrder: number;
  // ── Oracle auto-population (back-compat optional) ──
  oracleRef?: string;
  sector?: string;
  // item_ids of agenda tasks the user drag-linked to this weekly aim.
  linkedItemIds?: string[];
  // ids of monthly goals this weekly aim is tap-linked UP to (mirrors linkedItemIds).
  linkedGoalIds?: string[];
  dismissed?: boolean;
}

type ItemField = 'completed' | 'text' | 'time' | 'duration' | 'missed_reason' | 'note';
type ItemTarget = { scope: 'day' } | { scope: 'profile'; profileId: string };

const generateId = () => Math.random().toString(36).substring(2, 9);

function formatSeconds(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// Copied verbatim from src/lib/time-utils.ts (normalizeTime).
function normalizeTime(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (!s) return '';
  const amPmMatch = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?|am|pm|a|p)?$/);
  if (!amPmMatch) return raw.trim();
  let hours = parseInt(amPmMatch[1], 10);
  const minutes = amPmMatch[2] ?? '00';
  const meridiem = amPmMatch[3];
  let period = '';
  if (meridiem && meridiem.startsWith('p')) {
    period = 'PM';
    if (hours < 12) hours += 12;
  } else if (meridiem && meridiem.startsWith('a')) {
    period = 'AM';
    if (hours === 12) hours = 0;
  } else if (hours >= 13) {
    period = hours >= 12 ? 'PM' : 'AM';
    if (hours > 12) hours -= 12;
  } else {
    period = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) { hours = 12; period = 'AM'; }
  }
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes} ${period}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Minutes since midnight for an agenda "H:MM AM/PM" string; null if blank/unparseable. */
function agendaTimeMinutes(raw: string): number | null {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((raw ?? '').trim());
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === 'PM') h += 12;
  return h * 60 + parseInt(m[2], 10);
}

function dateKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysLocal(d: Date, delta: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + delta);
  return next;
}

function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

// Standard ISO-8601 week number/year (matches date-fns getISOWeek/getISOWeekYear).
function isoWeekAndYear(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return { week, year: d.getUTCFullYear() };
}

function weekKeyOf(d: Date): string {
  const { week, year } = isoWeekAndYear(d);
  return `${year}-W${pad2(week)}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0]}`;
}

function formatDisplayDate(d: Date): string {
  return `${WEEKDAYS[d.getDay()]} ${MONTHS_SHORT[d.getMonth()]} ${ordinal(d.getDate())}`;
}

function formatMonthFull(d: Date): string {
  return `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;
}

// ── Oracle spine → METIS auto-population helpers ──
// Deity id → display name, flattened from SECTOR_DEITY_MAP (e.g. 'sofia' →
// 'Sofia'). Falls back to a capitalized raw id if the id isn't catalogued.
const DEITY_NAMES: Record<string, string> = Object.fromEntries(
  Object.values(SECTOR_DEITY_MAP)
    .flat()
    .map((d) => [d.id, d.name])
);
function deityDisplayName(deityId: string): string {
  const raw = DEITY_NAMES[deityId] ?? deityId;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ── D.E.D.L.S structure (SortableRow.tsx DESIGN_CATEGORIES) ──
const DESIGN_CATEGORIES: { category: string; labels: string[] }[] = [
  { category: 'Directives', labels: ['Role', 'Rules', 'Results'] },
  { category: 'Essentials', labels: ['Needs'] },
  { category: 'Step', labels: ['Single step'] },
  { category: 'Implementation', labels: ['When & How'] },
  { category: 'Guide', labels: [''] },
  { category: 'Notes', labels: ['Outcome'] },
];

function getDefaultDesignRows(): DesignRowData[] {
  const rows: DesignRowData[] = [];
  for (const cat of DESIGN_CATEGORIES) {
    for (const label of cat.labels) rows.push({ label, value: '' });
  }
  return rows;
}

function createEmptyItem(): AgendaItem {
  return {
    id: generateId(),
    item_id: generateId(),
    completed: false,
    text: '',
    time: '',
    duration: '',
    sort_order: 0,
    nails: [],
    design: null,
    missed_reason: '',
    note: '',
  };
}

function createEmptyItems(): AgendaItem[] {
  return Array.from({ length: 7 }, (_, i) => ({ ...createEmptyItem(), sort_order: i }));
}

function parseDurationSeconds(d: string): number {
  const n = parseInt(d, 10);
  return Number.isNaN(n) || n <= 0 ? 0 : n * 60;
}

// ── Raw DB row shapes (untyped supabase client → cast defensively) ──
interface RawAgendaRow {
  id: string;
  item_id: string;
  completed: boolean | null;
  text: string | null;
  time: string | null;
  duration: string | null;
  sort_order: number | null;
  nails: unknown;
  design: unknown;
  missed_reason: string | null;
  note: string | null;
  updated_at: string | null;
  task_id?: string | null;
  sector?: string | null;
  design_id?: string | null;
  aim_ref?: string | null;
}

interface RawSettingsRow {
  agenda_profiles: unknown;
  monthly_goals: unknown;
  weekly_goals: unknown;
  metis_design_templates: unknown;
  metis_profiles_enabled: boolean | null;
  pomodoro_enabled: boolean | null;
  nail_timer_seconds: number | null;
  pomodoro_work_time: number | null;
  pomodoro_break_time: number | null;
  pomodoro_break_start_bell: string | null;
  pomodoro_break_end_bell: string | null;
  pomodoro_repeat: boolean | null;
}

// Gradient glass fill + inner top highlight, dropped as the first child of a
// panel (the panel supplies radius / overflow / hairline).
function GlassFill() {
  const { t } = useTheme();
  const styles = useStyles();
  return (
    <>
      <LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View pointerEvents="none" style={styles.glassHighlight} />
    </>
  );
}

// ── Nail timer row (NailRow.tsx). A stopwatch: counts UP `elapsed`,
// remaining time is derived as (nailTimerSeconds*60 - elapsed) exactly as
// the web component computes it. ──
function NailTimerRow({
  nail,
  nailTimerSeconds,
  onUpdateText,
  onUpdateElapsed,
  onRemove,
}: {
  nail: NailItem;
  nailTimerSeconds: number;
  onUpdateText: (text: string) => void;
  onUpdateElapsed: (elapsed: number) => void;
  onRemove: () => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(nail.elapsed);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!running) setElapsed(nail.elapsed);
  }, [nail.elapsed, running]);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const toggle = () => {
    if (running) {
      setRunning(false);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      onUpdateElapsed(elapsed);
    } else {
      setRunning(true);
      intervalRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    }
  };

  const remaining = Math.max(0, nailTimerSeconds * 60 - elapsed);

  return (
    <View style={styles.nailRow}>
      <Pressable style={[styles.nailToggle, running && styles.nailToggleRunning]} onPress={toggle}>
        <Text style={styles.nailToggleGlyph}>{running ? '⏸' : '▶'}</Text>
      </Pressable>
      <TextInput
        style={styles.nailTextInput}
        value={nail.text}
        onChangeText={onUpdateText}
        placeholder="What are you nailing down?"
        placeholderTextColor={t.ivoryFaint}
      />
      <Text style={[styles.nailRemaining, remaining <= 30 && styles.nailRemainingWarn]}>
        {formatSeconds(remaining)}
      </Text>
      <Pressable onPress={onRemove} hitSlop={8}>
        <Text style={styles.nailRemove}>×</Text>
      </Pressable>
    </View>
  );
}

// Approx height (px) of one collapsed agenda row incl. its gap — the finger
// travel that equals one slot when drag-reordering.
const DRAG_ROW_STEP = 58;

// ── One agenda item row (SortableRow.tsx), reused for both the day agenda
// and every profile's per-day items. ──
// The New ORDER sheet = the ORDER loop (Orient · Regulate · Direct · Execute ·
// Revise), each a prompt the user answers. Stored as design.rows [{label,value}].
const ORDER_FIELDS: { label: string; prompt: string }[] = [
  { label: 'Orient', prompt: 'What am I doing and why?' },
  { label: 'Regulate', prompt: 'What do I need?' },
  { label: 'Directions', prompt: 'What order will I do them in?' },
  { label: 'Execute', prompt: '' }, // leave blank
  { label: 'Revise', prompt: 'What has changed?' },
];
const makeOrderRows = (): DesignRowData[] => ORDER_FIELDS.map((f) => ({ label: f.label, value: '' }));

type TabId = 'themis' | 'nail' | 'notes' | 'duration' | 'info';
const ITEM_TABS: { id: TabId; label: string }[] = [
  { id: 'themis', label: 'THEMIS' },
  { id: 'nail', label: 'Nail' },
  { id: 'notes', label: 'Notes' },
  { id: 'duration', label: 'Dur' },
  { id: 'info', label: 'Info' },
];

interface ItemRowProps {
  item: AgendaItem;
  userId: string;
  nailTimerSeconds: number;
  pomodoro: PomodoroSettings;
  missReasons: string[];
  protocolGroups: ProtocolGroupPick[];
  protocols: ProtocolPickRow[];
  designTemplates: MetisDesignTemplate[];
  onUpdate: (field: ItemField, value: string | boolean) => void;
  onSetStatus: (completed: boolean, missedReason: string) => void;
  onUpdateDesign: (design: DesignData | null) => void;
  // ── Long-press status expand (Done/Missed/Auto-rescued/Manual reschedule).
  // Only wired for AGENDA — TODAY (day-scope) rows — see MetisTool's
  // renderItemRow. Profile-scope rows keep the plain tap/long-press behavior.
  canReschedule: boolean;
  onAutoRescue?: (item: AgendaItem) => void;
  onManualReschedule?: (item: AgendaItem) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDelete: () => void;
  onOpenTrajectory: () => void;
  listLabel: string | null;
  onAddNail: () => void;
  onUpdateNailText: (nailId: string, text: string) => void;
  onUpdateNailElapsed: (nailId: string, elapsed: number) => void;
  onRemoveNail: (nailId: string) => void;
  // ── Drag-to-link a task onto a WEEKLY aim row (task 4) ──
  rowCount: number;
  weeklyOpen: boolean;
  isLinkedToWeekly: boolean;
  getWeeklyHoverGoalId: (absoluteY: number) => string | null;
  onWeeklyHoverChange: (goalId: string | null) => void;
  onLinkToWeeklyGoal: (goalId: string) => void;
  // ── Tap-to-link (task 4b): mouse-friendly alternative to the drag-to-link
  // above — tapping the crosshair opens a picker of this week's aims. ──
  weeklyAims: { id: string; text: string; linked: boolean }[];
  onToggleWeeklyLink: (goalId: string) => void;
  /** Onboarding: this is the first AGENDA — TODAY row (registers metis.row1.* targets). */
  tourRow1?: boolean;
}

function ItemRow({
  item, userId, nailTimerSeconds, pomodoro, missReasons, protocolGroups, protocols, designTemplates,
  onUpdate, onSetStatus, onUpdateDesign, onMoveUp, onMoveDown, onDragStart, onDragEnd, onDelete,
  onOpenTrajectory, listLabel, onAddNail, onUpdateNailText, onUpdateNailElapsed, onRemoveNail,
  rowCount, weeklyOpen, isLinkedToWeekly, getWeeklyHoverGoalId, onWeeklyHoverChange, onLinkToWeeklyGoal,
  weeklyAims, onToggleWeeklyLink, canReschedule, onAutoRescue, onManualReschedule, tourRow1,
}: ItemRowProps) {
  const ob = useOnboarding();
  const tid = (name: string) => (tourRow1 ? `metis.row1.${name}` : '');
  const { t, c } = useTheme();
  const styles = useStyles();
  const [durRunning, setDurRunning] = useState(false);
  const [durRemaining, setDurRemaining] = useState(() => parseDurationSeconds(item.duration));
  const [lastSyncedDuration, setLastSyncedDuration] = useState(item.duration);

  // ── Redesign state: minimal row (status checkbox · title · ⋮ · time) + accordion tabs ──
  const [tabsOpen, setTabsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  // ── Long-press status expand: reveals Done/Missed/Auto-rescued/Manual
  // reschedule icon row via a plain RN `Animated` width+opacity tween (no
  // reanimated available in this app). ──
  const [statusExpanded, setStatusExpanded] = useState(false);
  const statusExpandAnim = useRef(new Animated.Value(0)).current;
  const STATUS_EXPAND_WIDTH = 4 * 26 + 3 * 6; // 4 icon buttons @ 26px + 6px gaps
  const expandStatus = () => {
    setStatusExpanded(true);
    Animated.timing(statusExpandAnim, { toValue: 1, duration: 220, useNativeDriver: false }).start();
  };
  const collapseStatus = () => {
    Animated.timing(statusExpandAnim, { toValue: 0, duration: 160, useNativeDriver: false }).start(() => {
      setStatusExpanded(false);
    });
  };
  // THEMIS tab: browse protocols (picker) → launch runner, or edit a New ORDER sheet.
  const [themisMode, setThemisMode] = useState<'picker' | 'order' | 'runner'>('picker');
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [selectedProtocolId, setSelectedProtocolId] = useState<string | null>(null);
  const [runningProtocolId, setRunningProtocolId] = useState<string | null>(null);
  const [designRows, setDesignRows] = useState<DesignRowData[]>(() => item.design?.rows ?? makeOrderRows());

  const status: 'completed' | 'missed' | 'none' = item.completed
    ? 'completed'
    : item.missed_reason
    ? 'missed'
    : 'none';

  const openTab = (id: TabId) => {
    setActiveTab((cur) => (cur === id ? null : id));
    if (id === 'themis') {
      setThemisMode('picker');
      setSelectedProtocolId(null);
    }
  };

  const openNewOrder = () => {
    setDesignRows(item.design?.rows ?? makeOrderRows());
    setThemisMode('order');
  };
  const setOrderRow = (index: number, value: string) => {
    setDesignRows((prev) => {
      const next = prev.map((r, i) => (i === index ? { ...r, value } : r));
      onUpdateDesign({ rows: next });
      return next;
    });
  };
  const applyTemplate = (tmpl: MetisDesignTemplate) => {
    const rows = tmpl.rows.map((r) => ({ label: r.label, value: r.value }));
    setDesignRows(rows);
    onUpdateDesign({ rows, templateId: tmpl.id });
  };

  const confirmDelete = () => {
    Alert.alert('Delete this task?', 'This removes the row and its content.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: onDelete },
    ]);
  };

  // ── Pomodoro auto-cycling (mirrors src/context/pomodoro-context.tsx).
  // Scoped per-row: starting THIS row's duration timer while pomodoro is
  // enabled treats the row's own duration as the work phase, then
  // auto-cycles work→break→work using pomodoro_work_time/break_time from
  // user_settings until the row is paused/reset. Remaining time is always
  // derived from a wall-clock deadline (Date.now() delta), not accumulated
  // ticks, so a brief background/foreground cycle doesn't drift. ──
  const [pomodoroPhase, setPomodoroPhaseState] = useState<'work' | 'break' | null>(null);
  const deadlineRef = useRef<number>(0); // epoch ms when the current phase ends
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickedThisSecondRef = useRef<Set<number>>(new Set());
  const breakBellRepeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Backgrounded/killed-app alert: id of the local notification scheduled
  // against the current deadline (see armDeadline / cancelScheduledNotif).
  const scheduledNotificationIdRef = useRef<string | null>(null);
  const itemTextRef = useRef(item.text);
  useEffect(() => { itemTextRef.current = item.text; }, [item.text]);

  const cancelScheduledNotif = useCallback(() => {
    const id = scheduledNotificationIdRef.current;
    scheduledNotificationIdRef.current = null;
    void cancelTimerNotification(id);
  }, []);

  // Refs mirroring `pomodoroPhase` state and the `pomodoro` settings prop —
  // read inside setInterval/AppState closures (which are only created once,
  // via empty-deps useCallback/useEffect) so they never see stale values.
  const pomodoroPhaseRef = useRef<'work' | 'break' | null>(null);
  useEffect(() => { pomodoroPhaseRef.current = pomodoroPhase; }, [pomodoroPhase]);
  const setPomodoroPhase = (phase: 'work' | 'break' | null) => {
    pomodoroPhaseRef.current = phase;
    setPomodoroPhaseState(phase);
  };
  const pomodoroRef = useRef(pomodoro);
  useEffect(() => { pomodoroRef.current = pomodoro; }, [pomodoro]);

  useEffect(() => {
    if (!durRunning && item.duration !== lastSyncedDuration) {
      setDurRemaining(parseDurationSeconds(item.duration));
      setLastSyncedDuration(item.duration);
    }
  }, [item.duration, durRunning, lastSyncedDuration]);

  const clearBreakBellRepeat = useCallback(() => {
    if (breakBellRepeatRef.current) { clearInterval(breakBellRepeatRef.current); breakBellRepeatRef.current = null; }
  }, []);

  const stopTicking = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    clearBreakBellRepeat();
    cancelScheduledNotif();
  }, [clearBreakBellRepeat, cancelScheduledNotif]);

  useEffect(() => () => stopTicking(), [stopTicking]);

  function handlePhaseComplete() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    clearBreakBellRepeat();
    // In-app expiry handler is running (app foregrounded) — cancel the
    // OS-scheduled notification for this same deadline before playing the
    // in-app chime, so the two never fire together (see notifications.ts
    // handler-config comment for the non-duplicating rationale).
    cancelScheduledNotif();
    const pomo = pomodoroRef.current;

    if (pomo.enabled && pomodoroPhaseRef.current === 'work') {
      // Work phase done → break start bell, enter break.
      playPomodoroBell(pomo.breakStartBell, /* isWorkEnd */ true);
      if (pomo.repeat) {
        // No background-music engine exists on mobile (documented deviation
        // — see file header); the closest audible analog to "repeat music
        // during break" is repeating the break-start bell periodically for
        // the length of the break.
        breakBellRepeatRef.current = setInterval(() => {
          playPomodoroBell(pomo.breakStartBell, true);
        }, 20000);
      }
      setPomodoroPhase('break');
      armDeadline(pomo.breakSeconds);
      return;
    }
    if (pomo.enabled && pomodoroPhaseRef.current === 'break') {
      // Break done → break end bell, back to work.
      clearBreakBellRepeat();
      playPomodoroBell(pomo.breakEndBell, false);
      setPomodoroPhase('work');
      armDeadline(pomo.workSeconds);
      return;
    }

    // Plain (non-pomodoro) duration timer finished.
    playWorkEndChime();
    setDurRunning(false);
    setPomodoroPhase(null);
    setDurRemaining(0);
  }

  // Recompute remaining seconds from the wall-clock deadline every second
  // (and immediately on mount/resume), rather than trusting accumulated
  // setInterval fire counts — this is what keeps the timer correct across
  // brief app backgrounding.
  const recomputeFromDeadline = useCallback(() => {
    const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
    setDurRemaining(remaining);
    // Countdown tick in the final 10s, once per whole second (mirrors web's
    // `playBeep(660, 0.1)` in SortableRow's duration-timer interval).
    if (remaining > 0 && remaining <= 10 && !tickedThisSecondRef.current.has(remaining)) {
      tickedThisSecondRef.current.add(remaining);
      playDurationTick();
    }
    if (remaining <= 0) {
      handlePhaseComplete();
    }
    return remaining;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Snap the displayed countdown to the wall-clock deadline the instant the
  // app returns to the foreground, instead of waiting up to 1s for the next
  // setInterval tick — this is what makes a brief background/foreground
  // cycle look instantaneous rather than "catching up."
  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active' && tickRef.current) recomputeFromDeadline();
    });
    return () => sub.remove();
  }, [recomputeFromDeadline]);

  const armDeadline = useCallback((seconds: number) => {
    const deadline = Date.now() + seconds * 1000;
    deadlineRef.current = deadline;
    tickedThisSecondRef.current = new Set();
    setDurRemaining(seconds);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => { recomputeFromDeadline(); }, 1000);

    // Schedule the backgrounded/killed-app fallback alert for this same
    // deadline. Cancel whatever was previously pending first (re-arming,
    // e.g. pomodoro phase transitions, always supersedes the prior alert).
    cancelScheduledNotif();
    const phase = pomodoroPhaseRef.current;
    const label = itemTextRef.current || 'Timer';
    const title = phase === 'work' ? `${label} — work time's up`
      : phase === 'break' ? `${label} — break time's up`
      : `${label} — time's up`;
    const body = phase === 'work' ? 'Work interval complete.'
      : phase === 'break' ? 'Break interval complete.'
      : 'Duration timer complete.';
    void scheduleTimerNotification(deadline, title, body).then((id) => {
      // Deadline may have already been superseded/cancelled by the time the
      // permission prompt/schedule call resolves — don't leak a stray id.
      if (deadlineRef.current === deadline) scheduledNotificationIdRef.current = id;
      else void cancelTimerNotification(id);
    });
  }, [recomputeFromDeadline, cancelScheduledNotif]);

  const startDurTimer = () => {
    if (pomodoro.enabled) {
      // Pomodoro engine owns the phase lengths from here — the row's own
      // "duration" field is only used as the initial work-phase length when
      // set; otherwise fall back to the user's configured work time.
      const initialWork = parseDurationSeconds(item.duration) > 0
        ? parseDurationSeconds(item.duration)
        : pomodoro.workSeconds;
      setDurRunning(true);
      setPomodoroPhase('work');
      armDeadline(initialWork);
      return;
    }
    if (durRemaining <= 0) return;
    setDurRunning(true);
    setPomodoroPhase(null);
    armDeadline(durRemaining);
  };
  const pauseDurTimer = () => {
    setDurRunning(false);
    stopTicking();
  };
  const resetDurTimer = () => {
    pauseDurTimer();
    setPomodoroPhase(null);
    setDurRemaining(parseDurationSeconds(item.duration));
  };

  const hasDuration = parseDurationSeconds(item.duration) > 0 || pomodoro.enabled;
  const durFinished = !pomodoro.enabled && hasDuration && durRemaining <= 0 && !durRunning;

  // ── Drag-to-reorder. Reordering re-renders this row (props change), so keep
  // the move callbacks in refs and build the Pan gesture ONCE (empty deps) —
  // a fresh gesture object each render would detach mid-drag and cancel it.
  // As the finger travels one row-height, swap this item up/down a slot; the
  // parent disables ScrollView scrolling while a drag is active (onDragStart/
  // onDragEnd) so the list doesn't fight the gesture.
  const [dragging, setDragging] = useState(false);
  const dragStepRef = useRef(0);
  const moveUpRef = useRef(onMoveUp);
  const moveDownRef = useRef(onMoveDown);
  const dragStartRef = useRef(onDragStart);
  const dragEndRef = useRef(onDragEnd);
  moveUpRef.current = onMoveUp;
  moveDownRef.current = onMoveDown;
  dragStartRef.current = onDragStart;
  dragEndRef.current = onDragEnd;

  // Drag-to-link (task 4): while dragging, the finger's absoluteY is checked
  // against measured WEEKLY-aim row bounds every update. Hovering a weekly
  // row FREEZES the step-reorder (dragStepRef stops advancing) so the far
  // travel distance down to the WEEKLY section never fires dozens of
  // moveDown() calls; releasing over that row links the task instead of
  // reordering. Kept in refs (mirroring moveUpRef/moveDownRef above) since
  // the gesture object is built once with empty deps.
  const rowCountRef = useRef(rowCount);
  const weeklyOpenRef = useRef(weeklyOpen);
  const getWeeklyHoverGoalIdRef = useRef(getWeeklyHoverGoalId);
  const onWeeklyHoverChangeRef = useRef(onWeeklyHoverChange);
  const onLinkToWeeklyGoalRef = useRef(onLinkToWeeklyGoal);
  rowCountRef.current = rowCount;
  weeklyOpenRef.current = weeklyOpen;
  getWeeklyHoverGoalIdRef.current = getWeeklyHoverGoalId;
  onWeeklyHoverChangeRef.current = onWeeklyHoverChange;
  onLinkToWeeklyGoalRef.current = onLinkToWeeklyGoal;
  const hoverGoalIdRef = useRef<string | null>(null);

  const dragGesture = useMemo(
    () =>
      Gesture.Pan()
        .activateAfterLongPress(140)
        .onStart(() => {
          dragStepRef.current = 0;
          hoverGoalIdRef.current = null;
          setDragging(true);
          dragStartRef.current();
          Vibration.vibrate(10);
        })
        .onUpdate((e) => {
          const hover = weeklyOpenRef.current ? getWeeklyHoverGoalIdRef.current(e.absoluteY) : null;
          if (hover !== hoverGoalIdRef.current) {
            hoverGoalIdRef.current = hover;
            onWeeklyHoverChangeRef.current(hover);
          }
          if (hover) return; // frozen — hovering the WEEKLY zone suspends reordering
          const rawStep = Math.round(e.translationY / DRAG_ROW_STEP);
          const bound = Math.max(0, rowCountRef.current - 1);
          const step = Math.max(-bound, Math.min(bound, rawStep));
          while (step > dragStepRef.current) { moveDownRef.current(); dragStepRef.current += 1; }
          while (step < dragStepRef.current) { moveUpRef.current(); dragStepRef.current -= 1; }
        })
        .onFinalize(() => {
          if (hoverGoalIdRef.current) {
            onLinkToWeeklyGoalRef.current(hoverGoalIdRef.current);
            Vibration.vibrate(10);
          }
          hoverGoalIdRef.current = null;
          onWeeklyHoverChangeRef.current(null);
          dragStepRef.current = 0;
          setDragging(false);
          dragEndRef.current();
        }),
    []
  );

  // Tap-to-link (task 4b): a quick tap on the same crosshair handle opens a
  // picker of this week's aims — mouse-friendly alternative to the
  // long-press+drag flow above (which is unreliable with a pointer device).
  // Gesture.Exclusive lets the Pan's activateAfterLongPress(140) win any
  // press held past that threshold; a quick tap resolves to Tap instead.
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  // Tour: the crosshair's aim picker opened on the first row.
  useEffect(() => {
    if (linkPickerOpen && tourRow1) tourBus.emit('metis:picker');
  }, [linkPickerOpen, tourRow1]);
  const tapGesture = useMemo(
    () => Gesture.Tap().onEnd(() => setLinkPickerOpen(true)),
    []
  );
  const crosshairGesture = useMemo(
    () => Gesture.Exclusive(dragGesture, tapGesture),
    [dragGesture, tapGesture]
  );

  const statusLabel = status === 'completed' ? 'Completed' : status === 'missed' ? 'Missed' : 'Status';

  return (
    <View style={[styles.itemCard, durRunning && styles.itemCardCurrent, dragging && styles.itemCardDragging]}>
      <GlassFill />
      {/* Minimal row: drag · status checkbox · title · ⋮ accordion · time */}
      <View style={styles.itemMainRow}>
        <TourBox id={tid('crosshair')}>
        <GestureDetector gesture={crosshairGesture}>
          <View style={styles.dragHandle} hitSlop={8}>
            <Crosshair size={17} color={dragging ? t.gold : t.ivorySoft} strokeWidth={2} />
          </View>
        </GestureDetector>
        </TourBox>
        {isLinkedToWeekly ? <Text style={styles.linkGlyph}>⛓</Text> : null}

        {/* Tap = toggle Completed (or cancel the expand, if open). Long-press
            = expand into 4 status options (Done/Missed/Auto-rescued/Manual
            reschedule) on day-scope rows; on profile rows (canReschedule
            false) long-press keeps the old behavior of opening the
            missed-reason picker directly. */}
        <View style={styles.statusControlWrap}>
          <Pressable
            style={[
              styles.statusCheckbox,
              status === 'completed' && styles.statusCheckboxDone,
              status === 'missed' && styles.statusCheckboxMissed,
            ]}
            onPress={() => {
              if (statusExpanded) { collapseStatus(); return; }
              onSetStatus(!item.completed, '');
            }}
            onLongPress={() => {
              if (!canReschedule) { setReasonOpen(true); return; }
              expandStatus();
            }}
            hitSlop={8}
          >
            {status === 'completed' ? <Text style={styles.statusCheckboxMark}>✓</Text> : null}
            {status === 'missed' ? <Text style={styles.statusCheckboxMissedMark}>!</Text> : null}
          </Pressable>

          {canReschedule && statusExpanded ? (
            <Animated.View
              style={[
                styles.statusExpandRow,
                {
                  width: statusExpandAnim.interpolate({ inputRange: [0, 1], outputRange: [0, STATUS_EXPAND_WIDTH] }),
                  opacity: statusExpandAnim,
                },
              ]}
            >
              <Pressable
                style={[styles.statusOptionBtn, styles.statusOptionDone]}
                onPress={() => { onSetStatus(true, ''); collapseStatus(); }}
                hitSlop={4}
              >
                <Check size={15} color={t.onGold} strokeWidth={2.5} />
              </Pressable>
              <Pressable
                style={[styles.statusOptionBtn, styles.statusOptionMissed]}
                onPress={() => { setReasonOpen(true); collapseStatus(); }}
                hitSlop={4}
              >
                <X size={15} color={t.onGold} strokeWidth={2.5} />
              </Pressable>
              <Pressable
                style={[styles.statusOptionBtn, styles.statusOptionAuto]}
                onPress={() => { onAutoRescue?.(item); collapseStatus(); }}
                hitSlop={4}
              >
                <RotateCcw size={15} color={t.onGold} strokeWidth={2.5} />
              </Pressable>
              <Pressable
                style={[styles.statusOptionBtn, styles.statusOptionManual]}
                onPress={() => { onManualReschedule?.(item); collapseStatus(); }}
                hitSlop={4}
              >
                <Clock size={15} color={t.onGold} strokeWidth={2.5} />
              </Pressable>
            </Animated.View>
          ) : null}
        </View>

        <TourBox id={tid('time')}>
        <TextInput
          style={styles.timeInputNarrow}
          value={item.time}
          onChangeText={(t) => onUpdate('time', t)}
          returnKeyType="done"
          onSubmitEditing={() => {
            const normalized = normalizeTime(item.time);
            if (normalized !== item.time) onUpdate('time', normalized);
            if (tourRow1 && normalized.trim()) tourBus.emit('metis:time');
          }}
          onBlur={() => {
            const normalized = normalizeTime(item.time);
            if (normalized !== item.time) onUpdate('time', normalized);
            if (tourRow1 && normalized.trim()) tourBus.emit('metis:time');
          }}
          placeholder="time"
          placeholderTextColor={t.ivoryFaint}
        />
        </TourBox>

        <TextInput
          style={[styles.itemTextInput, item.completed && styles.itemTextDone]}
          value={item.text}
          onChangeText={(t) => onUpdate('text', t)}
          placeholder="Enter agenda item..."
          placeholderTextColor={t.ivoryFaint}
        />

        <Pressable
          style={[styles.accordionToggle, tabsOpen && styles.accordionToggleOpen]}
          onPress={() => setTabsOpen((o) => !o)}
          hitSlop={8}
        >
          <MoreVertical size={18} color={tabsOpen ? t.goldBright : t.ivorySoft} strokeWidth={2} />
        </Pressable>
      </View>

      {/* Tabs (double-tap title to reveal) */}
      {tabsOpen ? (
        <View style={styles.tabsWrap}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabBar}>
            {ITEM_TABS.map((t) => (
              <Pressable
                key={t.id}
                style={[styles.tabChip, activeTab === t.id && styles.tabChipActive]}
                onPress={() => openTab(t.id)}
              >
                <Text style={[styles.tabChipText, activeTab === t.id && styles.tabChipTextActive]}>{t.label}</Text>
              </Pressable>
            ))}
            <Pressable style={styles.tabDeleteChip} onPress={confirmDelete}>
              <Text style={styles.tabDeleteText}>Delete</Text>
            </Pressable>
          </ScrollView>

          {activeTab ? (
            <View style={styles.tabPanel}>
              <GlassFill />
              {activeTab === 'themis' ? (
                <ThemisTabContent
                  userId={userId}
                  protocols={protocols}
                  protocolGroups={protocolGroups}
                  designTemplates={designTemplates}
                  designRows={designRows}
                  mode={themisMode}
                  expandedGroupId={expandedGroupId}
                  selectedProtocolId={selectedProtocolId}
                  runningProtocolId={runningProtocolId}
                  onNewOrder={openNewOrder}
                  onSetOrderRow={setOrderRow}
                  onApplyTemplate={applyTemplate}
                  onToggleGroup={(gid) => setExpandedGroupId((c) => (c === gid ? null : gid))}
                  onSelectProtocol={setSelectedProtocolId}
                  onApplyProtocol={() => {
                    if (selectedProtocolId) {
                      setRunningProtocolId(selectedProtocolId);
                      setThemisMode('runner');
                    }
                  }}
                  onBackToPicker={() => {
                    setRunningProtocolId(null);
                    setThemisMode('picker');
                  }}
                />
              ) : null}

              {activeTab === 'nail' ? (
                <View>
                  {item.nails.length === 0 ? (
                    <Text style={styles.tabHint}>No nail timers yet.</Text>
                  ) : (
                    item.nails.map((nail) => (
                      <NailTimerRow
                        key={nail.id}
                        nail={nail}
                        nailTimerSeconds={nailTimerSeconds}
                        onUpdateText={(text) => onUpdateNailText(nail.id, text)}
                        onUpdateElapsed={(elapsed) => onUpdateNailElapsed(nail.id, elapsed)}
                        onRemove={() => onRemoveNail(nail.id)}
                      />
                    ))
                  )}
                  <Pressable style={styles.tabAddButton} onPress={onAddNail}>
                    <Text style={styles.tabAddButtonText}>+ Add nail timer</Text>
                  </Pressable>
                </View>
              ) : null}

              {activeTab === 'notes' ? (
                <TextInput
                  style={styles.tabNoteArea}
                  value={item.note}
                  onChangeText={(t) => onUpdate('note', t)}
                  placeholder="Add a note for this track…"
                  placeholderTextColor={t.ivoryFaint}
                  multiline
                  textAlignVertical="top"
                />
              ) : null}

              {activeTab === 'duration' ? (
                <View style={styles.durationTab}>
                  <TextInput
                    style={styles.durationTabInput}
                    value={item.duration}
                    onChangeText={(t) => onUpdate('duration', t)}
                    placeholder="min"
                    placeholderTextColor={t.ivoryFaint}
                    keyboardType="number-pad"
                    editable={!durRunning}
                  />
                  <Pressable
                    style={[styles.timerToggle, durRunning && styles.timerToggleRunning]}
                    onPress={durRunning ? pauseDurTimer : startDurTimer}
                    disabled={!hasDuration && !durRunning}
                  >
                    <Text style={styles.timerToggleGlyph}>{durRunning ? '⏸' : '▶'}</Text>
                  </Pressable>
                  {pomodoroPhase ? (
                    <Text style={[styles.pomodoroPhaseLabel, pomodoroPhase === 'break' && styles.pomodoroPhaseLabelBreak]}>
                      {pomodoroPhase === 'work' ? 'Work' : 'Break'}
                    </Text>
                  ) : null}
                  {durRunning || durFinished ? (
                    <Text
                      style={[
                        styles.durationRemaining,
                        durFinished && styles.durationDone,
                        !durFinished && durRemaining <= 30 && styles.durationWarn,
                      ]}
                    >
                      {durFinished ? 'Done!' : formatSeconds(durRemaining)}
                    </Text>
                  ) : null}
                  {durFinished ? (
                    <Pressable onPress={resetDurTimer}>
                      <Text style={styles.resetLink}>reset</Text>
                    </Pressable>
                  ) : null}
                </View>
              ) : null}

              {activeTab === 'info' ? (
                <View>
                  <Text style={styles.infoLine}>
                    List: <Text style={styles.infoValue}>{listLabel ?? 'Daily agenda'}</Text>
                  </Text>
                  <Text style={styles.infoLine}>
                    Status: <Text style={styles.infoValue}>{statusLabel === 'Status' ? 'Not set' : statusLabel}</Text>
                    {status === 'missed' && item.missed_reason ? ` — ${item.missed_reason}` : ''}
                  </Text>
                  <Text style={styles.infoLine}>
                    Note: <Text style={styles.infoValue}>{item.note ? 'yes' : '—'}</Text>
                    {'   '}ORDER: <Text style={styles.infoValue}>{item.design ? 'yes' : '—'}</Text>
                  </Text>
                  <Pressable style={styles.tabAddButton} onPress={onOpenTrajectory}>
                    <Text style={styles.tabAddButtonText}>View full trajectory / history</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}

      {/* Long-press the checkbox → miss-reason picker (options from Settings → METIS) */}
      <Modal visible={reasonOpen} transparent animationType="fade" onRequestClose={() => setReasonOpen(false)}>
        <Pressable style={styles.pickerBackdrop} onPress={() => setReasonOpen(false)}>
          <View style={styles.pickerCard}>
            <GlassFill />
            <Text style={styles.pickerHeader}>Reason for miss</Text>
            <ScrollView style={{ maxHeight: 280 }}>
              {missReasons.length === 0 ? (
                <Text style={styles.tabHint}>No reasons configured. Add them in Settings → M.E.T.I.S.</Text>
              ) : (
                missReasons.map((r) => (
                  <Pressable
                    key={r}
                    style={styles.pickerOption}
                    onPress={() => { onSetStatus(false, r); setReasonOpen(false); }}
                  >
                    <Text style={styles.pickerOptionText}>{r}</Text>
                  </Pressable>
                ))
              )}
            </ScrollView>
            {status !== 'none' ? (
              <Pressable
                style={styles.pickerOption}
                onPress={() => { onSetStatus(false, ''); setReasonOpen(false); }}
              >
                <Text style={styles.pickerOptionMuted}>Clear status</Text>
              </Pressable>
            ) : null}
          </View>
        </Pressable>
      </Modal>

      {/* Tap the crosshair handle → link/unlink this task against WEEKLY aims
          (mouse-friendly alternative to the drag-to-link gesture above). */}
      <LinkPickerShell inline={ob.active} visible={linkPickerOpen} onClose={() => setLinkPickerOpen(false)} backdropStyle={styles.pickerBackdrop}>
          <Pressable style={styles.pickerCard} onPress={() => {}}>
            <GlassFill />
            <Text style={styles.pickerHeader}>Link to weekly aim</Text>
            {weeklyAims.length === 0 ? (
              <Text style={[styles.tabHint, { paddingHorizontal: 18, paddingBottom: 14 }]}>
                No weekly aims yet — add one in WEEKLY — AIMS.
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 280 }}>
                {weeklyAims.map((aim, aimIdx) => (
                  <TourBox key={aim.id} id={aimIdx === 0 ? tid('aim') : ''}>
                  <Pressable
                    style={styles.linkPickerRow}
                    onPress={() => {
                      onToggleWeeklyLink(aim.id);
                      if (tourRow1 && !aim.linked) {
                        tourBus.emit('metis:linked');
                        if (ob.active) setLinkPickerOpen(false);
                      }
                    }}
                  >
                    <View style={[styles.linkPickerCheckbox, aim.linked && styles.linkPickerCheckboxOn]}>
                      {aim.linked ? <Text style={styles.linkPickerCheckMark}>✓</Text> : null}
                    </View>
                    <Text style={styles.linkPickerRowText} numberOfLines={2}>{aim.text || 'Untitled aim'}</Text>
                  </Pressable>
                  </TourBox>
                ))}
              </ScrollView>
            )}
            <Pressable style={styles.linkPickerDoneButton} onPress={() => setLinkPickerOpen(false)}>
              <Text style={styles.linkPickerDoneText}>Done</Text>
            </Pressable>
          </Pressable>
      </LinkPickerShell>
    </View>
  );
}

/** Link picker host: native Modal normally; inline panel while the tour is active so the tour lock covers it. */
function LinkPickerShell({ inline, visible, onClose, backdropStyle, children }: {
  inline: boolean; visible: boolean; onClose: () => void; backdropStyle: any; children: React.ReactNode;
}) {
  const body = (
    <Pressable style={backdropStyle} onPress={onClose}>{children}</Pressable>
  );
  // Inline = in-flow under the row (the row card clips absolute overlays); no backdrop so nothing else is tappable-through.
  if (inline) return visible ? <View style={{ paddingVertical: 8, alignItems: 'center' }}>{children}</View> : null;
  return <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>{body}</Modal>;
}

// ── THEMIS tab: New ORDER (per-task design sheet) or browse categories →
// protocol → Apply (launches the full THEMIS runner inline). ──
function ThemisTabContent({
  userId,
  protocols,
  protocolGroups,
  designTemplates,
  designRows,
  mode,
  expandedGroupId,
  selectedProtocolId,
  runningProtocolId,
  onNewOrder,
  onSetOrderRow,
  onApplyTemplate,
  onToggleGroup,
  onSelectProtocol,
  onApplyProtocol,
  onBackToPicker,
}: {
  userId: string;
  protocols: ProtocolPickRow[];
  protocolGroups: ProtocolGroupPick[];
  designTemplates: MetisDesignTemplate[];
  designRows: DesignRowData[];
  mode: 'picker' | 'order' | 'runner';
  expandedGroupId: string | null;
  selectedProtocolId: string | null;
  runningProtocolId: string | null;
  onNewOrder: () => void;
  onSetOrderRow: (index: number, value: string) => void;
  onApplyTemplate: (t: MetisDesignTemplate) => void;
  onToggleGroup: (groupId: string) => void;
  onSelectProtocol: (id: string) => void;
  onApplyProtocol: () => void;
  onBackToPicker: () => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  if (mode === 'runner' && runningProtocolId) {
    return (
      <View>
        <Pressable style={styles.orderBackRow} onPress={onBackToPicker}>
          <Text style={styles.orderBackText}>‹ Back to protocols</Text>
        </Pressable>
        <ProtocolDetailScreen protocolId={runningProtocolId} userId={userId} onBack={onBackToPicker} hideHelp />
      </View>
    );
  }

  if (mode === 'order') {
    return (
      <View>
        <Pressable style={styles.orderBackRow} onPress={onBackToPicker}>
          <Text style={styles.orderBackText}>‹ Back to protocols</Text>
        </Pressable>
        {designTemplates.length > 0 ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.templateRow}>
            {designTemplates.map((t) => (
              <Pressable key={t.id} style={styles.templateChip} onPress={() => onApplyTemplate(t)}>
                <Text style={styles.templateChipText}>{t.name}</Text>
              </Pressable>
            ))}
          </ScrollView>
        ) : null}
        {ORDER_FIELDS.map((f, i) => (
          <View key={f.label} style={styles.orderField}>
            <Text style={styles.orderFieldLabel}>{f.label}</Text>
            <TextInput
              style={styles.orderFieldInput}
              value={designRows[i]?.value ?? ''}
              onChangeText={(t) => onSetOrderRow(i, t)}
              multiline
              placeholder={f.prompt || 'Leave blank'}
              placeholderTextColor={t.ivoryFaint}
            />
          </View>
        ))}
      </View>
    );
  }

  // picker
  const ungrouped = protocols.filter((p) => !p.group_id);
  return (
    <View>
      <Pressable style={styles.newOrderButton} onPress={onNewOrder}>
        <Text style={styles.newOrderButtonText}>＋ New ORDER</Text>
      </Pressable>

      {protocols.length === 0 && protocolGroups.length === 0 ? (
        <Text style={styles.tabHint}>No protocols yet — create them in THEMIS.</Text>
      ) : null}

      {protocolGroups.map((g) => {
        const inGroup = protocols.filter((p) => p.group_id === g.id);
        const expanded = expandedGroupId === g.id;
        return (
          <View key={g.id}>
            <Pressable style={styles.protoGroupRow} onPress={() => onToggleGroup(g.id)}>
              <Text style={styles.protoGroupChevron}>{expanded ? '▾' : '▸'}</Text>
              <Text style={styles.protoGroupName}>{g.name}</Text>
              <Text style={styles.protoGroupCount}>{inGroup.length}</Text>
            </Pressable>
            {expanded
              ? inGroup.map((p) => (
                  <ProtocolPickItem
                    key={p.id}
                    title={p.title}
                    selected={selectedProtocolId === p.id}
                    onPress={() => onSelectProtocol(p.id)}
                  />
                ))
              : null}
          </View>
        );
      })}

      {ungrouped.map((p) => (
        <ProtocolPickItem
          key={p.id}
          title={p.title}
          selected={selectedProtocolId === p.id}
          onPress={() => onSelectProtocol(p.id)}
        />
      ))}

      {selectedProtocolId ? (
        <Pressable style={styles.applyButton} onPress={onApplyProtocol}>
          <Text style={styles.applyButtonText}>Apply &amp; run</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function ProtocolPickItem({ title, selected, onPress }: { title: string; selected: boolean; onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable style={[styles.protoItem, selected && styles.protoItemSelected]} onPress={onPress}>
      <Text style={[styles.protoItemText, selected && styles.protoItemTextSelected]} numberOfLines={1}>
        {title || 'Untitled protocol'}
      </Text>
      {selected ? <Text style={styles.protoItemCheck}>✓</Text> : null}
    </Pressable>
  );
}

// ── FLOW orb view (task 5) — task → aim → goal chain, same visual language as
// TrajectoryView's orb chain (circle + label) but a self-contained render:
// evenly-spaced columns per tier, absolutely positioned so the connector
// lines (react-native-svg <Line>) can be computed from the same coordinates
// without an onLayout measurement pass. Kept simple per spec: no wrap-aware
// line recalculation, single-row-per-tier. ──
const FLOW_ORB = { task: 44, aim: 60, goal: 76 };
const FLOW_TIER_Y = { goal: 50, aim: 210, task: 380 };
const FLOW_WRAP_W = 92;

function flowRowXs(n: number, orbSize: number, containerWidth: number): number[] {
  const gap = 18;
  const total = n * orbSize + Math.max(0, n - 1) * gap;
  const startX = Math.max(FLOW_WRAP_W / 2, (containerWidth - total) / 2 + orbSize / 2);
  return Array.from({ length: n }, (_, i) => startX + i * (orbSize + gap));
}

function FlowOrb({
  x, y, size, text, done, tint,
}: {
  x: number; y: number; size: number; text: string; done?: boolean; tint?: 'aim';
}) {
  const styles = useStyles();
  return (
    <View style={[styles.flowOrbWrap, { left: x - FLOW_WRAP_W / 2, top: y, width: FLOW_WRAP_W }]}>
      <View
        style={[
          styles.flowOrb,
          { width: size, height: size, borderRadius: size / 2 },
          tint === 'aim' && styles.flowOrbAim,
          done && styles.flowOrbDone,
        ]}
      >
        <Text style={[styles.flowOrbGlyph, done && styles.flowOrbGlyphDone]} numberOfLines={1}>
          {text.slice(0, 3).toUpperCase()}
        </Text>
      </View>
      <Text style={styles.flowOrbLabel} numberOfLines={2}>
        {text.slice(0, 14)}
      </Text>
    </View>
  );
}

function FlowOrbView({
  dayItems, weeklyForPeriod, monthlyForPeriod,
}: {
  dayItems: AgendaItem[];
  weeklyForPeriod: WeeklyGoal[];
  monthlyForPeriod: MonthlyGoal[];
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const containerWidth = Math.max(280, SCREEN_WIDTH - 24);
  const tasks = dayItems.filter((i) => i.text.trim().length > 0);
  const aims = weeklyForPeriod.filter((g) => g.text.trim().length > 0);
  const goals = monthlyForPeriod.filter((g) => g.text.trim().length > 0);

  const taskXs = flowRowXs(tasks.length, FLOW_ORB.task, containerWidth);
  const aimXs = flowRowXs(aims.length, FLOW_ORB.aim, containerWidth);
  const goalXs = flowRowXs(goals.length, FLOW_ORB.goal, containerWidth);
  const taskIndexById = new Map(tasks.map((t, i) => [t.item_id, i]));

  const lines: { key: string; x1: number; y1: number; x2: number; y2: number }[] = [];
  const goalIndexById = new Map(goals.map((g, i) => [g.id, i]));
  aims.forEach((aim, aIdx) => {
    const ax = aimXs[aIdx];
    const ay = FLOW_TIER_Y.aim + FLOW_ORB.aim / 2;
    for (const itemId of aim.linkedItemIds ?? []) {
      const tIdx = taskIndexById.get(itemId);
      if (tIdx == null) continue;
      lines.push({
        key: `t-${itemId}-${aim.id}`,
        x1: taskXs[tIdx],
        y1: FLOW_TIER_Y.task + FLOW_ORB.task / 2,
        x2: ax,
        y2: ay,
      });
    }

    // Explicit tap-links (aim → monthly goal) drawn first so the dedupe set
    // below suppresses a redundant sector-match line for the same pair.
    const drawnGoalPairs = new Set<string>();
    for (const goalId of aim.linkedGoalIds ?? []) {
      const gIdx = goalIndexById.get(goalId);
      if (gIdx == null) continue;
      drawnGoalPairs.add(goalId);
      lines.push({
        key: `a-link-${aim.id}-${goalId}`,
        x1: ax,
        y1: ay,
        x2: goalXs[gIdx],
        y2: FLOW_TIER_Y.goal + FLOW_ORB.goal / 2,
      });
    }

    const aimSector = aim.sector ?? aim.oracleRef?.split(':')[1];
    if (!aimSector) return;
    goals.forEach((goal, gIdx) => {
      if (drawnGoalPairs.has(goal.id)) return; // avoid duplicate line for an already tap-linked pair
      const goalSector = goal.sector ?? goal.oracleRef?.split(':')[1];
      if (goalSector && goalSector === aimSector) {
        lines.push({
          key: `a-sector-${aim.id}-${goal.id}`,
          x1: ax,
          y1: ay,
          x2: goalXs[gIdx],
          y2: FLOW_TIER_Y.goal + FLOW_ORB.goal / 2,
        });
      }
    });
  });

  const contentHeight = FLOW_TIER_Y.task + FLOW_ORB.task + 50;

  return (
    <View style={styles.panel}>
        <GlassFill />
      <View style={styles.panelHeaderSecondary}>
        <Text style={styles.panelHeaderTitleSmall}>FLOW — TASKS → AIMS → GOALS</Text>
      </View>
      <View style={styles.flowCanvasOuter}>
        <View style={[styles.flowCanvas, { width: containerWidth, height: contentHeight }]}>
          <Svg width={containerWidth} height={contentHeight} style={StyleSheet.absoluteFill}>
            {lines.map((l) => (
              <Line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke={t.gold} strokeWidth={2} opacity={0.6} />
            ))}
          </Svg>

          {goals.length === 0 && aims.length === 0 && tasks.length === 0 ? (
            <Text style={styles.emptyInline}>Nothing to show yet — add tasks, aims, or goals.</Text>
          ) : null}

          {goals.map((goal, i) => (
            <FlowOrb key={goal.id} x={goalXs[i]} y={FLOW_TIER_Y.goal} size={FLOW_ORB.goal} text={goal.text} done={goal.completed} />
          ))}
          {aims.map((aim, i) => (
            <FlowOrb key={aim.id} x={aimXs[i]} y={FLOW_TIER_Y.aim} size={FLOW_ORB.aim} text={aim.text} done={aim.completed} tint="aim" />
          ))}
          {tasks.map((task, i) => (
            <FlowOrb key={task.item_id} x={taskXs[i]} y={FLOW_TIER_Y.task} size={FLOW_ORB.task} text={task.text} done={task.completed} />
          ))}
        </View>
      </View>
    </View>
  );
}

export function MetisTool(_props: ToolProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  // ── Day-keyed agenda (agenda_items table) ──
  const [currentDate, setCurrentDate] = useState(new Date());
  const dateKey = dateKeyOf(currentDate);

  // Read-only KYRS tasks + KRNOS events scheduled today (shared Today data), so
  // METIS shows the whole day. Never written to agenda_items.
  const shared = useOptionalSharedTodayData();
  const externalToday = useMemo(() => {
    if (!shared || dateKey !== dateKeyOf(new Date())) return [];
    return shared.dayEntries.filter((e) => e.source !== 'agenda');
  }, [shared, dateKey]);

  const [dayItems, setDayItems] = useState<AgendaItem[]>([]);
  const [dayLoading, setDayLoading] = useState(true);
  const [dayError, setDayError] = useState<string | null>(null);

  const dayItemsRef = useRef<AgendaItem[]>([]);
  useEffect(() => { dayItemsRef.current = dayItems; }, [dayItems]);
  const dateKeyRef = useRef(dateKey);
  useEffect(() => { dateKeyRef.current = dateKey; }, [dateKey]);
  const dayDirtyRef = useRef(false);
  // item_ids (keyed `date|item_id`) METIS has loaded or saved. The save diff only
  // deletes rows in this set, so rows inserted elsewhere (KYRS pick) survive.
  const knownIdsRef = useRef<Set<string>>(new Set());
  const [reloadTick, setReloadTick] = useState(0);
  const pendingReloadRef = useRef(false);
  const daySavingRef = useRef(false);
  const daySaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setDayLoading(true);
    (async () => {
      const { data, error: err } = await supabase
        .from(AGENDA_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('date', dateKey)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      if (err) {
        setDayError(err.message);
        setDayItems(createEmptyItems());
      } else {
        setDayError(null);
        for (const r of (data ?? []) as RawAgendaRow[]) knownIdsRef.current.add(`${dateKey}|${r.item_id}`);
        const rows = ((data ?? []) as RawAgendaRow[]).map((row) => ({
          id: row.id,
          item_id: row.item_id,
          completed: !!row.completed,
          text: row.text ?? '',
          time: row.time ?? '',
          duration: row.duration ?? '',
          sort_order: row.sort_order ?? 0,
          nails: Array.isArray(row.nails) ? (row.nails as NailItem[]) : [],
          design: (row.design as DesignData | null) ?? null,
          missed_reason: row.missed_reason ?? '',
          note: row.note ?? '',
          task_id: row.task_id ?? null,
          sector: row.sector ?? null,
          design_id: row.design_id ?? null,
          aim_ref: row.aim_ref ?? null,
          _updated: row.updated_at ?? '',
        }));
        // Dedup by item_id — keep the row with the newest updated_at.
        const seen = new Map<string, (typeof rows)[number]>();
        for (const r of rows) {
          const existing = seen.get(r.item_id);
          if (!existing || r._updated > existing._updated) seen.set(r.item_id, r);
        }
        const deduped: AgendaItem[] = [...seen.values()]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map(({ _updated, ...rest }) => rest);
        setDayItems(deduped.length > 0 ? deduped : createEmptyItems());
      }
      dayDirtyRef.current = false;
      setDayLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, dateKey, reloadTick]);

  // KYRS pick inserted an agenda row: refetch (deferred until our own save is done).
  useEffect(() => {
    return subscribeAgendaRowsChanged(() => {
      if (dayDirtyRef.current || daySavingRef.current) pendingReloadRef.current = true;
      else setReloadTick((n) => n + 1);
    });
  }, []);

  // Onboarding (METIS stop): pre-fill today's empty agenda from KYRS tasks in priority order.
  const obMetis = useOnboarding();
  // Tour: the whole agenda list is the live area while the user adds their own task / reorders.
  const agendaTour = useTourTarget('metis.agenda');
  const obPrefillRef = useRef(false);
  const inMetisStop = obMetis.active && !!obMetis.step?.id.startsWith('s4_');
  useEffect(() => {
    if (!inMetisStop || !userId || dayLoading || obPrefillRef.current) return;
    if (dayDirtyRef.current || daySavingRef.current) return;
    obPrefillRef.current = true;
    void prefillAgendaFromKyrs(userId).then((res) => {
      if (res.error) setDayError(res.error);
      else if (res.created > 0) setReloadTick((n) => n + 1);
    });
  }, [inMetisStop, userId, dayLoading]);

  const saveDayItemsNow = useCallback(async () => {
    if (!userId || daySavingRef.current || !dayDirtyRef.current) return;
    daySavingRef.current = true;
    dayDirtyRef.current = false;
    const currentItems = dayItemsRef.current;
    const dk = dateKeyRef.current;
    const toSave = currentItems.filter(
      (i) => i.text || i.time || i.duration || i.completed || i.nails.length > 0 || i.design || i.missed_reason || i.note
    );
    try {
      if (toSave.length > 0) {
        const rows = toSave.map((item, idx) => {
          const row: Record<string, unknown> = {
            user_id: userId,
            date: dk,
            item_id: item.item_id,
            completed: item.completed,
            text: item.text,
            time: item.time,
            duration: item.duration,
            sort_order: idx,
            nails: item.nails,
            design: item.design,
            task_id: item.task_id ?? null,
            sector: item.sector ?? null,
            design_id: item.design_id ?? null,
            aim_ref: item.aim_ref ?? null,
            updated_at: new Date().toISOString(),
          };
          if (item.missed_reason) row.missed_reason = item.missed_reason;
          if (item.note) row.note = item.note;
          return row;
        });
        const { error: insertErr } = await supabase.from(AGENDA_TABLE).insert(rows);
        if (insertErr) throw insertErr;
        // Dedup: keep only the newest row per item_id, drop anything no
        // longer present in toSave (mirrors the web save function exactly).
        const { data: allRows } = await supabase
          .from(AGENDA_TABLE)
          .select('id, item_id, updated_at')
          .eq('user_id', userId)
          .eq('date', dk)
          .order('updated_at', { ascending: false });
        if (allRows) {
          const seen = new Set<string>();
          const toDelete: string[] = [];
          for (const r of allRows as { id: string; item_id: string }[]) {
            if (seen.has(r.item_id)) toDelete.push(r.id);
            else seen.add(r.item_id);
          }
          const saveIds = new Set(toSave.map((s) => s.item_id));
          for (const id of saveIds) knownIdsRef.current.add(`${dk}|${id}`);
          for (const r of allRows as { id: string; item_id: string }[]) {
            if (!saveIds.has(r.item_id) && !toDelete.includes(r.id) && knownIdsRef.current.has(`${dk}|${r.item_id}`)) toDelete.push(r.id);
          }
          if (toDelete.length > 0) {
            await supabase.from(AGENDA_TABLE).delete().in('id', toDelete);
          }
        }
      } else {
        const known = [...knownIdsRef.current].filter((k) => k.startsWith(`${dk}|`)).map((k) => k.slice(dk.length + 1));
        if (known.length > 0) {
          await supabase.from(AGENDA_TABLE).delete().eq('user_id', userId).eq('date', dk).in('item_id', known);
        }
      }
      // Tell other screens (Dashboard TODAY, Now) the agenda changed.
      notifyDataChanged();
    } catch (err) {
      setDayError(err instanceof Error ? err.message : 'Failed to save agenda items');
      dayDirtyRef.current = true;
    } finally {
      daySavingRef.current = false;
      if (pendingReloadRef.current && !dayDirtyRef.current) {
        pendingReloadRef.current = false;
        setReloadTick((n) => n + 1);
      }
      if (dayDirtyRef.current) {
        daySaveTimerRef.current = setTimeout(() => { void saveDayItemsNow(); }, 300);
      }
    }
  }, [userId]);

  const scheduleDaySave = useCallback(() => {
    dayDirtyRef.current = true;
    if (daySaveTimerRef.current) clearTimeout(daySaveTimerRef.current);
    daySaveTimerRef.current = setTimeout(() => { void saveDayItemsNow(); }, 900);
  }, [saveDayItemsNow]);

  useEffect(() => () => {
    if (daySaveTimerRef.current) clearTimeout(daySaveTimerRef.current);
    void saveDayItemsNow();
  }, [saveDayItemsNow]);

  const handlePrevDay = () => {
    if (daySaveTimerRef.current) clearTimeout(daySaveTimerRef.current);
    void saveDayItemsNow();
    setCurrentDate((prev) => addDaysLocal(prev, -1));
  };
  const handleNextDay = () => {
    if (daySaveTimerRef.current) clearTimeout(daySaveTimerRef.current);
    void saveDayItemsNow();
    setCurrentDate((prev) => addDaysLocal(prev, 1));
  };

  // ── Settings-backed state: profiles, monthly/weekly goals, design
  // templates, nail timer duration, pomodoro toggle — all columns on
  // user_settings (see src/context/settings-context.tsx). ──
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<AgendaProfile[]>([]);
  const [monthlyGoals, setMonthlyGoals] = useState<MonthlyGoal[]>([]);
  const [weeklyGoals, setWeeklyGoals] = useState<WeeklyGoal[]>([]);
  const [designTemplates, setDesignTemplates] = useState<MetisDesignTemplate[]>([]);
  const [missReasons, setMissReasons] = useState<string[]>([]);
  // THEMIS protocol picker data (for each item's THEMIS tab).
  const [protocolGroups, setProtocolGroups] = useState<ProtocolGroupPick[]>([]);
  const [protocols, setProtocols] = useState<ProtocolPickRow[]>([]);
  // Whether the time-segment PROFILES panel is shown. Toggled in Settings →
  // METIS (MetisEditor), persisted as user_settings.metis_profiles_enabled.
  // Default off: METIS opens as a single daily agenda.
  const [profilesEnabled, setProfilesEnabled] = useState(false);
  const [pomodoroEnabled, setPomodoroEnabled] = useState(false);
  const [nailTimerSeconds, setNailTimerSeconds] = useState(300);
  // Pomodoro work/break cycle settings (see PomodoroSettings + PomodoroEditor.tsx
  // in ../olmps/settings — same user_settings columns, same defaults as web's
  // settings-context.tsx defaultAppSettings).
  const [pomodoroWorkTime, setPomodoroWorkTime] = useState(20);
  const [pomodoroBreakTime, setPomodoroBreakTime] = useState(5);
  const [pomodoroBreakStartBell, setPomodoroBreakStartBell] = useState('bell-1');
  const [pomodoroBreakEndBell, setPomodoroBreakEndBell] = useState('bell-2');
  const [pomodoroRepeat, setPomodoroRepeat] = useState(true);

  const profilesRef = useRef<AgendaProfile[]>([]);
  useEffect(() => { profilesRef.current = profiles; }, [profiles]);
  const monthlyGoalsRef = useRef<MonthlyGoal[]>([]);
  useEffect(() => { monthlyGoalsRef.current = monthlyGoals; }, [monthlyGoals]);
  const weeklyGoalsRef = useRef<WeeklyGoal[]>([]);
  useEffect(() => { weeklyGoalsRef.current = weeklyGoals; }, [weeklyGoals]);
  const pomodoroEnabledRef = useRef(false);
  useEffect(() => { pomodoroEnabledRef.current = pomodoroEnabled; }, [pomodoroEnabled]);

  const settingsDirtyRef = useRef(false);
  const settingsSavingRef = useRef(false);
  const settingsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from(SETTINGS_TABLE)
        .select(
          'agenda_profiles, monthly_goals, weekly_goals, metis_design_templates, metis_profiles_enabled, pomodoro_enabled, nail_timer_seconds, ' +
            'pomodoro_work_time, pomodoro_break_time, pomodoro_break_start_bell, pomodoro_break_end_bell, pomodoro_repeat, metis_miss_reasons'
        )
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setSettingsError(err.message);
      } else {
        setSettingsError(null);
        const row = data as RawSettingsRow | null;
        setProfiles(Array.isArray(row?.agenda_profiles) ? (row!.agenda_profiles as AgendaProfile[]) : []);
        setMonthlyGoals(Array.isArray(row?.monthly_goals) ? (row!.monthly_goals as MonthlyGoal[]) : []);
        setWeeklyGoals(Array.isArray(row?.weekly_goals) ? (row!.weekly_goals as WeeklyGoal[]) : []);
        setDesignTemplates(
          Array.isArray(row?.metis_design_templates) ? (row!.metis_design_templates as MetisDesignTemplate[]) : []
        );
        {
          const rawReasons = (row as unknown as { metis_miss_reasons?: unknown } | null)?.metis_miss_reasons;
          setMissReasons(Array.isArray(rawReasons) ? (rawReasons as unknown[]).filter((r): r is string => typeof r === 'string') : []);
        }
        setProfilesEnabled(!!row?.metis_profiles_enabled);
        setPomodoroEnabled(!!row?.pomodoro_enabled);
        setNailTimerSeconds(typeof row?.nail_timer_seconds === 'number' ? row!.nail_timer_seconds : 300);
        setPomodoroWorkTime(typeof row?.pomodoro_work_time === 'number' ? row!.pomodoro_work_time : 20);
        setPomodoroBreakTime(typeof row?.pomodoro_break_time === 'number' ? row!.pomodoro_break_time : 5);
        setPomodoroBreakStartBell(row?.pomodoro_break_start_bell ?? 'bell-1');
        setPomodoroBreakEndBell(row?.pomodoro_break_end_bell ?? 'bell-2');
        setPomodoroRepeat(row?.pomodoro_repeat ?? true);
      }
      setSettingsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // ── Oracle spine → auto-populate MONTHLY/WEEKLY (task 3) ──
  const [oracleSpine, setOracleSpine] = useState<OracleSpine | null>(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const spine = await loadOracleSpine(supabase, userId);
      if (!cancelled) setOracleSpine(spine);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Load THEMIS protocols + groups once (for the per-item THEMIS tab picker).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [pRes, gRes] = await Promise.all([
        supabase.from('protocols').select('id, title, group_id, order').eq('user_id', userId).order('order'),
        supabase.from('protocol_groups').select('id, name, order').eq('user_id', userId).order('order'),
      ]);
      if (cancelled) return;
      if (Array.isArray(pRes.data)) setProtocols(pRes.data as ProtocolPickRow[]);
      if (Array.isArray(gRes.data)) setProtocolGroups(gRes.data as ProtocolGroupPick[]);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const saveSettingsNow = useCallback(async () => {
    if (!userId || settingsSavingRef.current || !settingsDirtyRef.current) return;
    settingsSavingRef.current = true;
    settingsDirtyRef.current = false;
    try {
      const { error: err } = await supabase.from(SETTINGS_TABLE).upsert(
        {
          user_id: userId,
          agenda_profiles: profilesRef.current,
          monthly_goals: monthlyGoalsRef.current,
          weekly_goals: weeklyGoalsRef.current,
          pomodoro_enabled: pomodoroEnabledRef.current,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );
      if (err) throw err;
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to save settings');
      settingsDirtyRef.current = true;
    } finally {
      settingsSavingRef.current = false;
    }
  }, [userId]);

  const scheduleSettingsSave = useCallback(() => {
    settingsDirtyRef.current = true;
    if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => { void saveSettingsNow(); }, 900);
  }, [saveSettingsNow]);

  useEffect(() => () => {
    if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
    void saveSettingsNow();
  }, [saveSettingsNow]);

  // ── Generic item-list access, shared by the day agenda and every
  // profile's per-day items ──
  const getTargetItems = useCallback(
    (target: ItemTarget): AgendaItem[] => {
      if (target.scope === 'day') return dayItems;
      const p = profiles.find((pr) => pr.id === target.profileId);
      if (!p) return [createEmptyItem()];
      const byDate = p.itemsByDate?.[dateKey];
      return byDate && byDate.length > 0 ? byDate : [createEmptyItem()];
    },
    [dayItems, profiles, dateKey]
  );

  const setTargetItems = useCallback(
    (target: ItemTarget, items: AgendaItem[]) => {
      if (target.scope === 'day') {
        setDayItems(items);
        scheduleDaySave();
      } else {
        setProfiles((prev) =>
          prev.map((p) =>
            p.id === target.profileId
              ? { ...p, itemsByDate: { ...(p.itemsByDate ?? {}), [dateKey]: items } }
              : p
          )
        );
        scheduleSettingsSave();
      }
    },
    [dateKey, scheduleDaySave, scheduleSettingsSave]
  );

  const updateItemField = useCallback(
    (target: ItemTarget, itemId: string, field: ItemField, value: string | boolean) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? ({ ...i, [field]: value } as AgendaItem) : i
      );
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  // Status = Completed / Missed / (none). Set both `completed` and
  // `missed_reason` in ONE update so neither clobbers the other (two separate
  // updateItemField calls in the same tick would race on stale state).
  const setItemStatus = useCallback(
    (target: ItemTarget, itemId: string, completed: boolean, missedReason: string) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? { ...i, completed, missed_reason: missedReason } : i
      );
      setTargetItems(target, items);
      // Linked KYRS task: ticking the agenda row completes the task (and
      // unticking re-opens it). Lazy builder — must .then() to fire.
      const linked = items.find((i) => i.item_id === itemId);
      if (userId && linked?.task_id) {
        void supabase
          .from('tasks')
          .update({ done: completed, updated_at: new Date().toISOString() })
          .eq('id', linked.task_id)
          .eq('user_id', userId)
          .then(({ error: taskErr }) => {
            if (taskErr) setDayError(taskErr.message);
          });
      }
    },
    [getTargetItems, setTargetItems, userId]
  );

  // Promote a read-only KYRS row (batch 1) into the editable agenda, carrying
  // the task's link columns so ticking it later completes the task.
  const promoteTaskToAgenda = useCallback(
    (e: { taskId?: string | null; title: string; start: Date; end: Date; sector?: string | null; designId?: string | null; aimRef?: string | null }) => {
      if (!e.taskId) return;
      const mins = Math.max(1, Math.round((e.end.getTime() - e.start.getTime()) / 60000));
      const base = createEmptyItem();
      const next: AgendaItem = {
        ...base,
        text: e.title,
        time: normalizeTime(e.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })),
        duration: String(mins),
        task_id: e.taskId,
        sector: e.sector ?? null,
        design_id: e.designId ?? null,
        aim_ref: e.aimRef ?? null,
      };
      const cur = dayItemsRef.current;
      const emptyIdx = cur.findIndex((i) => !i.text && !i.time && !i.duration && !i.completed && i.nails.length === 0);
      const items = emptyIdx >= 0
        ? cur.map((i, idx) => (idx === emptyIdx ? { ...next, id: i.id, item_id: i.item_id, sort_order: i.sort_order } : i))
        : [...cur, { ...next, sort_order: cur.length }];
      setDayItems(items);
      scheduleDaySave();
    },
    [scheduleDaySave]
  );

  const addNailTo = useCallback(
    (target: ItemTarget, itemId: string) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? { ...i, nails: [...i.nails, { id: generateId(), text: '', elapsed: 0 }] } : i
      );
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  const updateNailTextIn = useCallback(
    (target: ItemTarget, itemId: string, nailId: string, text: string) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? { ...i, nails: i.nails.map((n) => (n.id === nailId ? { ...n, text } : n)) } : i
      );
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  const updateNailElapsedIn = useCallback(
    (target: ItemTarget, itemId: string, nailId: string, elapsed: number) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? { ...i, nails: i.nails.map((n) => (n.id === nailId ? { ...n, elapsed } : n)) } : i
      );
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  const removeNailFrom = useCallback(
    (target: ItemTarget, itemId: string, nailId: string) => {
      const items = getTargetItems(target).map((i) =>
        i.item_id === itemId ? { ...i, nails: i.nails.filter((n) => n.id !== nailId) } : i
      );
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  const updateDesignFor = useCallback(
    (target: ItemTarget, itemId: string, design: DesignData | null) => {
      const items = getTargetItems(target).map((i) => (i.item_id === itemId ? { ...i, design } : i));
      setTargetItems(target, items);
    },
    [getTargetItems, setTargetItems]
  );

  const addRowTo = useCallback(
    (target: ItemTarget) => {
      setTargetItems(target, [...getTargetItems(target), createEmptyItem()]);
    },
    [getTargetItems, setTargetItems]
  );

  // Native adaptation: the web page has no explicit "delete row" — an item
  // is dropped simply by clearing every field, so the save filter never
  // persists it. This exposes that same end-state via one tap, which is
  // friendlier on a phone than manually blanking five fields.
  const deleteItemFrom = useCallback(
    (target: ItemTarget, itemId: string) => {
      setTargetItems(target, getTargetItems(target).filter((i) => i.item_id !== itemId));
    },
    [getTargetItems, setTargetItems]
  );

  const moveItemIn = useCallback(
    (target: ItemTarget, itemId: string, dir: -1 | 1) => {
      const items = getTargetItems(target);
      const idx = items.findIndex((i) => i.item_id === itemId);
      if (idx < 0) return;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= items.length) return;
      const next = [...items];
      const tmp = next[idx];
      next[idx] = next[newIdx];
      next[newIdx] = tmp;
      setTargetItems(target, next);
    },
    [getTargetItems, setTargetItems]
  );

  // ── Trajectory view (ledger #2) — resolves the item's containing PROFILE
  // label as its "list", since agenda items carry no sector/list column of
  // their own (see TrajectoryView.tsx header comment). Day-scope items have
  // no profile, so they always render the unlinked state. Opened from an
  // item's Info tab. ──
  const [trajectoryTarget, setTrajectoryTarget] = useState<{ text: string; listLabel: string | null } | null>(null);

  // ── Auto-rescue / manual reschedule (AGENDA — TODAY only). Agenda items
  // have no linkage to the KYRS scheduling engine (confirmed — no "real"
  // auto-scheduler path exists to invoke), so auto-rescue is a working
  // fallback: push the item to the next day at the same time. Both this and
  // manual reschedule perform a direct Supabase INSERT-at-target-date +
  // DELETE-at-today write, bypassing the debounced day save entirely so the
  // move is atomic and not racing the 900ms-debounced autosave. Row shape
  // copied verbatim from saveDayItemsNow's `rows` map above. ──
  const moveAgendaItemToDate = useCallback(
    async (item: AgendaItem, targetDateKey: string, targetTime: string): Promise<{ error?: string }> => {
      if (!userId) return { error: 'Not signed in' };
      const row: Record<string, unknown> = {
        user_id: userId,
        date: targetDateKey,
        item_id: generateId(),
        completed: false,
        text: item.text,
        time: targetTime,
        duration: item.duration,
        sort_order: item.sort_order,
        nails: item.nails,
        design: item.design,
        task_id: item.task_id ?? null,
        sector: item.sector ?? null,
        design_id: item.design_id ?? null,
        aim_ref: item.aim_ref ?? null,
        updated_at: new Date().toISOString(),
      };
      if (item.note) row.note = item.note;
      const { error: insertErr } = await supabase.from(AGENDA_TABLE).insert(row);
      if (insertErr) return { error: insertErr.message };
      const { error: deleteErr } = await supabase.from(AGENDA_TABLE).delete().eq('id', item.id);
      if (deleteErr) return { error: deleteErr.message };
      notifyDataChanged();
      return {};
    },
    [userId]
  );

  const autoRescueItem = useCallback(
    async (item: AgendaItem) => {
      const tomorrowKey = dateKeyOf(addDaysLocal(currentDate, 1));
      const { error: moveErr } = await moveAgendaItemToDate(item, tomorrowKey, item.time);
      if (moveErr) {
        Alert.alert('Reschedule failed', moveErr);
        return;
      }
      setDayItems((prev) => prev.filter((i) => i.id !== item.id));
      const timeSuffix = item.time && item.time !== 'time' ? ` at ${item.time}` : '';
      Alert.alert('Rescheduled', `"${item.text}" moved to tomorrow${timeSuffix}.`);
    },
    [currentDate, moveAgendaItemToDate]
  );

  // Manual reschedule modal (blue clock option), styled to mirror the
  // Clio/Kyrs reschedule-dialog pattern (see file-level task notes — no
  // shared component exists to import, so the layout is replicated locally
  // with this file's own tokens).
  const [rescheduleTarget, setRescheduleTarget] = useState<AgendaItem | null>(null);
  const [rescheduleDateInput, setRescheduleDateInput] = useState('');
  const [rescheduleTimeInput, setRescheduleTimeInput] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const openManualReschedule = useCallback(
    (item: AgendaItem) => {
      setRescheduleTarget(item);
      setRescheduleDateInput(dateKey);
      setRescheduleTimeInput(item.time && item.time !== 'time' ? item.time : '09:00');
      setRescheduleError(null);
    },
    [dateKey]
  );

  const closeManualReschedule = useCallback(() => {
    setRescheduleTarget(null);
    setRescheduleError(null);
  }, []);

  const saveManualReschedule = useCallback(async () => {
    if (!rescheduleTarget) return;
    const targetDate = rescheduleDateInput.trim();
    const targetTime = rescheduleTimeInput.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(targetDate) || !/^\d{2}:\d{2}$/.test(targetTime)) {
      Alert.alert('Invalid date/time', 'Use yyyy-MM-dd and HH:mm.');
      return;
    }
    const item = rescheduleTarget;
    if (targetDate === dateKey) {
      // "Rescheduled" to the same day it's already on — just update its time.
      updateItemField({ scope: 'day' }, item.item_id, 'time', targetTime);
      closeManualReschedule();
      return;
    }
    setRescheduleSaving(true);
    setRescheduleError(null);
    const { error: moveErr } = await moveAgendaItemToDate(item, targetDate, targetTime);
    setRescheduleSaving(false);
    if (moveErr) {
      Alert.alert('Reschedule failed', moveErr);
      setRescheduleError(moveErr);
      return;
    }
    setDayItems((prev) => prev.filter((i) => i.id !== item.id));
    closeManualReschedule();
    Alert.alert('Rescheduled', `"${item.text}" moved to ${targetDate} at ${targetTime}.`);
  }, [rescheduleTarget, rescheduleDateInput, rescheduleTimeInput, dateKey, updateItemField, moveAgendaItemToDate, closeManualReschedule]);

  // ── Monthly / weekly goals ──
  const monthKey = monthKeyOf(currentDate);
  const weekKey = weekKeyOf(currentDate);
  // Oracle-seeded rows are soft-deleted via `dismissed` — filter them out of
  // both rendering and the seeding-dedupe check below reads the UNFILTERED
  // ref (monthlyGoalsRef/weeklyGoalsRef) so a dismissed row never resurrects.
  const monthlyForPeriod = monthlyGoals
    .filter((g) => g.monthKey === monthKey && !g.dismissed)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const weeklyForPeriod = weeklyGoals
    .filter((g) => g.weekKey === weekKey && !g.dismissed)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Keep the ATLAS-seeded rows in step with the current ladder, for the CURRENT month/week:
  // MONTHLY = one row per sector "<DEITY> — <Goal>" (ref goal:<sector>:<deity>), WEEKLY = one row
  // per Aim (ref aim:<sector>:<aimId>). Seeded rows whose text changed are rewritten; rows whose
  // Endeavour/deity/Aim is gone are dropped unless already ticked (history stays); new ones are
  // added. A row the user dismissed stays dismissed (it still counts as present).
  useEffect(() => {
    // Only once the saved goals are loaded, so a reconcile never saves over them.
    if (!oracleSpine || !userId || !settingsLoaded || settingsError) return;
    const wantMonthly = new Map<string, { text: string; sector: Sector; order: number }>();
    const wantWeekly = new Map<string, { text: string; sector: Sector; order: number; aimId: string }>();
    SECTORS.forEach((sector, sIdx) => {
      const node = oracleSpine[sector];
      if (!node) return;
      if (node.goal) {
        const deityName = deityDisplayName(node.goal);
        const what = atlasOf(node).goal.text || node.endeavour;
        wantMonthly.set(`goal:${sector}:${node.goal}`, { text: what ? `${deityName} — ${what}` : deityName, sector, order: 1000 + sIdx });
      }
      effectiveAims(node).forEach((aim, aIdx) => {
        wantWeekly.set(`aim:${sector}:${aim.id}`, {
          text: aim.cadence ? `${aim.label} · ${aim.cadence}` : aim.label,
          sector,
          order: 1000 + sIdx * 100 + aIdx,
          aimId: aim.id,
        });
      });
    });

    let changed = false;
    const reconcile = <G extends { oracleRef?: string; text: string; completed: boolean }>(
      rows: G[],
      inPeriod: (g: G) => boolean,
      prefix: string,
      want: Map<string, { text: string }>,
      make: (ref: string) => G,
    ): G[] => {
      const seen = new Set<string>();
      const out: G[] = [];
      for (const g of rows) {
        if (!inPeriod(g) || !g.oracleRef?.startsWith(prefix)) {
          out.push(g);
          continue;
        }
        const w = want.get(g.oracleRef);
        seen.add(g.oracleRef);
        if (!w) {
          if (g.completed) out.push(g);
          else changed = true;
          continue;
        }
        if (!g.completed && g.text !== w.text) {
          out.push({ ...g, text: w.text });
          changed = true;
        } else out.push(g);
      }
      for (const ref of want.keys()) {
        if (seen.has(ref)) continue;
        out.push(make(ref));
        changed = true;
      }
      return out;
    };

    const nextMonthly = reconcile(monthlyGoalsRef.current, (g) => g.monthKey === monthKey, 'goal:', wantMonthly, (ref) => {
      const w = wantMonthly.get(ref)!;
      return { id: `mg-oracle-${w.sector}-${monthKey}`, monthKey, text: w.text, completed: false, sortOrder: w.order, oracleRef: ref, sector: w.sector };
    });
    const nextWeekly = reconcile(weeklyGoalsRef.current, (g) => g.weekKey === weekKey, 'aim:', wantWeekly, (ref) => {
      const w = wantWeekly.get(ref)!;
      return { id: `wg-oracle-${w.sector}-${w.aimId}-${weekKey}`, weekKey, text: w.text, completed: false, sortOrder: w.order, oracleRef: ref, sector: w.sector, linkedItemIds: [] };
    });
    if (!changed) return;
    setMonthlyGoals(nextMonthly);
    setWeeklyGoals(nextWeekly);
    scheduleSettingsSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oracleSpine, userId, monthKey, weekKey, settingsLoaded, settingsError]);

  const addMonthlyGoal = () => {
    const maxOrder = monthlyForPeriod.reduce((m, g) => Math.max(m, g.sortOrder), -1);
    const goal: MonthlyGoal = { id: `mg-${Date.now()}-${generateId()}`, monthKey, text: '', completed: false, sortOrder: maxOrder + 1 };
    setMonthlyGoals((prev) => [...prev, goal]);
    scheduleSettingsSave();
  };
  const updateMonthlyGoal = (id: string, patch: Partial<MonthlyGoal>) => {
    setMonthlyGoals((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    scheduleSettingsSave();
  };
  const deleteMonthlyGoal = (id: string) => {
    // Oracle-seeded rows (carry `oracleRef`) are soft-deleted via `dismissed`
    // instead of removed — a hard delete would resurrect on the next
    // Oracle-seeding pass for this month.
    setMonthlyGoals((prev) =>
      prev.reduce<MonthlyGoal[]>((acc, g) => {
        if (g.id !== id) { acc.push(g); return acc; }
        if (g.oracleRef) acc.push({ ...g, dismissed: true });
        return acc;
      }, [])
    );
    scheduleSettingsSave();
  };

  // Reorder a MONTHLY/WEEKLY goal within its own period list (never across
  // periods) — mirrors moveItemIn's swap-with-neighbor approach but swaps
  // sortOrder values (rather than array position) since these lists are
  // filtered+sorted views over the full monthlyGoals/weeklyGoals arrays.
  const moveMonthlyGoal = (id: string, dir: -1 | 1) => {
    const idx = monthlyForPeriod.findIndex((g) => g.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= monthlyForPeriod.length) return;
    const a = monthlyForPeriod[idx];
    const b = monthlyForPeriod[newIdx];
    setMonthlyGoals((prev) =>
      prev.map((g) => {
        if (g.id === a.id) return { ...g, sortOrder: b.sortOrder };
        if (g.id === b.id) return { ...g, sortOrder: a.sortOrder };
        return g;
      })
    );
    scheduleSettingsSave();
  };
  const moveWeeklyGoal = (id: string, dir: -1 | 1) => {
    const idx = weeklyForPeriod.findIndex((g) => g.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= weeklyForPeriod.length) return;
    const a = weeklyForPeriod[idx];
    const b = weeklyForPeriod[newIdx];
    setWeeklyGoals((prev) =>
      prev.map((g) => {
        if (g.id === a.id) return { ...g, sortOrder: b.sortOrder };
        if (g.id === b.id) return { ...g, sortOrder: a.sortOrder };
        return g;
      })
    );
    scheduleSettingsSave();
  };

  const addWeeklyGoal = () => {
    const maxOrder = weeklyForPeriod.reduce((m, g) => Math.max(m, g.sortOrder), -1);
    const goal: WeeklyGoal = { id: `wg-${Date.now()}-${generateId()}`, weekKey, text: '', completed: false, sortOrder: maxOrder + 1 };
    setWeeklyGoals((prev) => [...prev, goal]);
    scheduleSettingsSave();
  };
  const updateWeeklyGoal = (id: string, patch: Partial<WeeklyGoal>) => {
    setWeeklyGoals((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
    scheduleSettingsSave();
  };
  const deleteWeeklyGoal = (id: string) => {
    // See deleteMonthlyGoal — oracle-seeded rows are soft-deleted, never
    // hard-removed, so they don't resurrect on the next seeding pass.
    setWeeklyGoals((prev) =>
      prev.reduce<WeeklyGoal[]>((acc, g) => {
        if (g.id !== id) { acc.push(g); return acc; }
        if (g.oracleRef) acc.push({ ...g, dismissed: true });
        return acc;
      }, [])
    );
    scheduleSettingsSave();
  };

  // ── Panel expand/collapse (mirrors the web Accordion defaults: profiles
  // open, monthly/weekly collapsed; AGENDA now defaults OPEN — task 2 — so
  // today's tasks are visible without an extra tap). ──
  const [profilesOpen, setProfilesOpen] = useState(true);
  const [agendaOpen, setAgendaOpen] = useState(true);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);

  // ── FLOW toggle (task 5) — session-only, not persisted. ──
  const [flowView, setFlowView] = useState(false);

  // ── Drag-to-link a task onto a WEEKLY aim row (task 4) ──
  const weeklyRowRefs = useRef<Map<string, View | null>>(new Map());
  const weeklyRowLayoutsRef = useRef<Map<string, { y: number; height: number }>>(new Map());
  const [highlightedWeeklyGoalId, setHighlightedWeeklyGoalId] = useState<string | null>(null);

  const remeasureWeeklyRows = useCallback(() => {
    weeklyRowRefs.current.forEach((view, goalId) => {
      if (!view) return;
      view.measureInWindow((_x, y, _width, height) => {
        weeklyRowLayoutsRef.current.set(goalId, { y, height });
      });
    });
  }, []);

  // Re-measure whenever the WEEKLY section opens or its row set changes —
  // window coordinates only make sense once the rows have actually laid out.
  useEffect(() => {
    if (!weeklyOpen) return;
    const t = setTimeout(remeasureWeeklyRows, 60);
    return () => clearTimeout(t);
  }, [weeklyOpen, weeklyForPeriod.length, remeasureWeeklyRows]);

  const getWeeklyHoverGoalId = useCallback((absoluteY: number): string | null => {
    for (const [goalId, layout] of weeklyRowLayoutsRef.current.entries()) {
      if (absoluteY >= layout.y && absoluteY <= layout.y + layout.height) return goalId;
    }
    return null;
  }, []);

  const onWeeklyHoverChange = useCallback((goalId: string | null) => {
    setHighlightedWeeklyGoalId(goalId);
  }, []);

  const linkItemToWeeklyGoal = useCallback(
    (goalId: string, itemId: string) => {
      setWeeklyGoals((prev) =>
        prev.map((g) => {
          if (g.id !== goalId) return g;
          const existing = g.linkedItemIds ?? [];
          if (existing.includes(itemId)) return g;
          return { ...g, linkedItemIds: [...existing, itemId] };
        })
      );
      scheduleSettingsSave();
    },
    [scheduleSettingsSave]
  );

  // Tap-to-link (task 4b): unlink counterpart to linkItemToWeeklyGoal, plus a
  // combined toggle so the tap-picker can flip link state with one call.
  const unlinkItemFromWeeklyGoal = useCallback(
    (goalId: string, itemId: string) => {
      setWeeklyGoals((prev) =>
        prev.map((g) => {
          if (g.id !== goalId) return g;
          const existing = g.linkedItemIds ?? [];
          if (!existing.includes(itemId)) return g;
          return { ...g, linkedItemIds: existing.filter((id) => id !== itemId) };
        })
      );
      scheduleSettingsSave();
    },
    [scheduleSettingsSave]
  );

  const toggleItemWeeklyLink = useCallback(
    (goalId: string, itemId: string) => {
      const goal = weeklyGoals.find((g) => g.id === goalId);
      const already = (goal?.linkedItemIds ?? []).includes(itemId);
      if (already) unlinkItemFromWeeklyGoal(goalId, itemId);
      else linkItemToWeeklyGoal(goalId, itemId);
    },
    [weeklyGoals, linkItemToWeeklyGoal, unlinkItemFromWeeklyGoal]
  );

  // Tap-to-link (weekly aim → monthly goal): toggles monthlyGoalId inside
  // that weekly aim's linkedGoalIds. Mirrors toggleItemWeeklyLink above.
  const toggleWeeklyGoalLink = useCallback(
    (weeklyGoalId: string, monthlyGoalId: string) => {
      setWeeklyGoals((prev) =>
        prev.map((g) => {
          if (g.id !== weeklyGoalId) return g;
          const existing = g.linkedGoalIds ?? [];
          const already = existing.includes(monthlyGoalId);
          return {
            ...g,
            linkedGoalIds: already
              ? existing.filter((id) => id !== monthlyGoalId)
              : [...existing, monthlyGoalId],
          };
        })
      );
      scheduleSettingsSave();
    },
    [scheduleSettingsSave]
  );

  // Per-row picker state for the weekly aim → monthly goal link modal
  // (single shared modal; holds the weekly goal id whose picker is open).
  const [aimLinkPickerFor, setAimLinkPickerFor] = useState<string | null>(null);

  // item_ids linked to any of THIS week's aims — drives the ⛓ indicator glyph.
  const linkedItemIdSet = useMemo(() => {
    const s = new Set<string>();
    for (const g of weeklyForPeriod) for (const id of g.linkedItemIds ?? []) s.add(id);
    return s;
  }, [weeklyForPeriod]);

  // ── Profiles ──
  const addProfile = () => {
    const p: AgendaProfile = { id: generateId(), label: 'New Profile', itemsByDate: {} };
    setProfiles((prev) => [...prev, p]);
    scheduleSettingsSave();
  };
  const updateProfileLabel = (id: string, label: string) => {
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, label } : p)));
    scheduleSettingsSave();
  };
  const deleteProfile = (id: string) => {
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    scheduleSettingsSave();
  };
  const confirmDeleteProfile = (profile: AgendaProfile) => {
    Alert.alert(
      'Delete this profile?',
      'This will permanently remove the profile and all of its agenda items. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => deleteProfile(profile.id) },
      ]
    );
  };
  const updateProfileTimes = (id: string, startTime: string | null, endTime: string | null) => {
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, startTime, endTime } : p)));
    scheduleSettingsSave();
  };

  // Which profile "now" falls in, based on HH:MM start/end (wraps midnight).
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const currentProfileId = useMemo(() => {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const toMin = (t?: string | null) => {
      if (!t) return null;
      const [h, m] = t.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };
    for (const p of profiles) {
      const s = toMin(p.startTime);
      const e = toMin(p.endTime);
      if (s == null || e == null) continue;
      const inRange = s <= e ? mins >= s && mins < e : mins >= s || mins < e;
      if (inRange) return p.id;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, nowTick]);

  const [expandedProfiles, setExpandedProfiles] = useState<Set<string>>(new Set());
  const lastAutoExpandedRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentProfileId && currentProfileId !== lastAutoExpandedRef.current) {
      lastAutoExpandedRef.current = currentProfileId;
      setExpandedProfiles(new Set([currentProfileId]));
    }
  }, [currentProfileId]);

  const toggleProfileExpanded = (id: string) => {
    setExpandedProfiles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePomodoro = (value: boolean) => {
    setPomodoroEnabled(value);
    scheduleSettingsSave();
  };

  const [timeSegmentsOpen, setTimeSegmentsOpen] = useState(false);
  // True while an item is being drag-reordered — freezes the ScrollView so the
  // vertical drag doesn't fight the scroll.
  const [dragActive, setDragActive] = useState(false);

  const loading = dayLoading || !settingsLoaded;
  const error = dayError || settingsError;

  // NOTE: this useMemo must stay above the early returns below — hooks cannot
  // run conditionally, and `loading` is true on the first render (so an early
  // return here would skip the hook and desync the hook order on later renders).
  const pomodoroSettings: PomodoroSettings = useMemo(
    () => ({
      enabled: pomodoroEnabled,
      workSeconds: pomodoroWorkTime * 60,
      breakSeconds: pomodoroBreakTime * 60,
      breakStartBell: pomodoroBreakStartBell,
      breakEndBell: pomodoroBreakEndBell,
      repeat: pomodoroRepeat,
    }),
    [pomodoroEnabled, pomodoroWorkTime, pomodoroBreakTime, pomodoroBreakStartBell, pomodoroBreakEndBell, pomodoroRepeat]
  );

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyTitle}>Sign in to use METIS</Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={t.goldBright} />
      </View>
    );
  }

  const renderItemRow = (target: ItemTarget, item: AgendaItem, idx: number, arr: AgendaItem[]) => {
    const listLabel = target.scope === 'profile'
      ? profiles.find((p) => p.id === target.profileId)?.label ?? null
      : null;
    return (
      <ItemRow
        key={item.item_id}
        item={item}
        userId={userId!}
        nailTimerSeconds={nailTimerSeconds}
        pomodoro={pomodoroSettings}
        missReasons={missReasons}
        protocolGroups={protocolGroups}
        protocols={protocols}
        designTemplates={designTemplates}
        listLabel={listLabel}
        onUpdate={(field, value) => updateItemField(target, item.item_id, field, value)}
        onSetStatus={(completed, reason) => setItemStatus(target, item.item_id, completed, reason)}
        onUpdateDesign={(design) => updateDesignFor(target, item.item_id, design)}
        onMoveUp={() => moveItemIn(target, item.item_id, -1)}
        onMoveDown={() => moveItemIn(target, item.item_id, 1)}
        onDragStart={() => setDragActive(true)}
        onDragEnd={() => setDragActive(false)}
        onDelete={() => deleteItemFrom(target, item.item_id)}
        onOpenTrajectory={() => setTrajectoryTarget({ text: item.text, listLabel })}
        onAddNail={() => addNailTo(target, item.item_id)}
        onUpdateNailText={(nailId, text) => updateNailTextIn(target, item.item_id, nailId, text)}
        onUpdateNailElapsed={(nailId, elapsed) => updateNailElapsedIn(target, item.item_id, nailId, elapsed)}
        onRemoveNail={(nailId) => removeNailFrom(target, item.item_id, nailId)}
        canReschedule={target.scope === 'day'}
        onAutoRescue={target.scope === 'day' ? (it) => void autoRescueItem(it) : undefined}
        onManualReschedule={target.scope === 'day' ? (it) => openManualReschedule(it) : undefined}
        rowCount={arr.length}
        weeklyOpen={weeklyOpen}
        isLinkedToWeekly={linkedItemIdSet.has(item.item_id)}
        getWeeklyHoverGoalId={getWeeklyHoverGoalId}
        onWeeklyHoverChange={onWeeklyHoverChange}
        onLinkToWeeklyGoal={(goalId) => linkItemToWeeklyGoal(goalId, item.item_id)}
        weeklyAims={weeklyForPeriod.map((g) => ({
          id: g.id,
          text: g.text,
          linked: (g.linkedItemIds ?? []).includes(item.item_id),
        }))}
        onToggleWeeklyLink={(goalId) => toggleItemWeeklyLink(goalId, item.item_id)}
        tourRow1={target.scope === 'day' && idx === 0}
      />
    );
  };

  return (
    <View style={styles.root}>
      <ScrollView contentContainerStyle={styles.content} scrollEnabled={!dragActive}>
        {/* "?" — explains the whole screen. */}
        <View style={styles.helpRow}>
          <ScreenHelp help={metisHelp} />
        </View>
        {error ? <Text style={styles.error}>{error}</Text> : null}

        {/* ─── FLOW toggle (task 5) — slim standalone row when PROFILES is off;
             folded into the PROFILES header (next to the pomodoro switch)
             when PROFILES is on, below. Rendered unconditionally either way. ── */}
        {!profilesEnabled ? (
          <View style={styles.flowToggleRow}>
            <Text style={styles.flowToggleLabel}>FLOW</Text>
            <Switch
              value={flowView}
              onValueChange={setFlowView}
              trackColor={{ true: c.lime, false: c.glassRim }}
              thumbColor={flowView ? t.gold : t.ivory}
            />
          </View>
        ) : null}

        {/* ─── PROFILES (primary panel; hidden unless enabled in Settings) ── */}
        {profilesEnabled ? (
        <View style={styles.panel}>
        <GlassFill />
          <Pressable style={styles.panelHeader} onPress={() => setProfilesOpen((v) => !v)}>
            <Text style={styles.panelHeaderTitle}>PROFILES</Text>
            <View style={styles.panelHeaderRight}>
              <Text style={styles.setsLabel}>Sets</Text>
              <Switch
                value={pomodoroEnabled}
                onValueChange={togglePomodoro}
                trackColor={{ true: c.lime, false: c.glassRim }}
                thumbColor={pomodoroEnabled ? t.gold : t.ivory}
              />
              <Text style={styles.setsLabel}>Flow</Text>
              <Switch
                value={flowView}
                onValueChange={setFlowView}
                trackColor={{ true: c.lime, false: c.glassRim }}
                thumbColor={flowView ? t.gold : t.ivory}
              />
              <Pressable
                onPress={(e) => { e.stopPropagation?.(); setTimeSegmentsOpen(true); }}
                hitSlop={8}
              >
                <Text style={styles.panelHeaderGlyph}>⋯</Text>
              </Pressable>
              <Text style={styles.panelHeaderGlyph}>{profilesOpen ? '⌃' : '⌄'}</Text>
            </View>
          </Pressable>

          {profilesOpen ? (
            <View style={styles.panelBody}>
              <View style={styles.dateNavRow}>
                <Pressable onPress={handlePrevDay} hitSlop={8}>
                  <Text style={styles.dateNavArrow}>‹</Text>
                </Pressable>
                <Text style={styles.dateNavLabel}>{formatDisplayDate(currentDate)}</Text>
                <Pressable onPress={handleNextDay} hitSlop={8}>
                  <Text style={styles.dateNavArrow}>›</Text>
                </Pressable>
              </View>

              {profiles.length === 0 ? (
                <Text style={styles.emptyInline}>No profiles yet. Add one to get started.</Text>
              ) : null}

              {profiles.map((profile) => {
                const target: ItemTarget = { scope: 'profile', profileId: profile.id };
                const items = getTargetItems(target);
                const expanded = expandedProfiles.has(profile.id);
                return (
                  <View key={profile.id} style={styles.profileCard}>
                    <GlassFill />
                    <Pressable style={styles.profileHeader} onPress={() => toggleProfileExpanded(profile.id)}>
                      <TextInput
                        style={styles.profileLabelInput}
                        value={profile.label}
                        onChangeText={(t) => updateProfileLabel(profile.id, t)}
                        placeholder="PROFILE NAME"
                        placeholderTextColor={t.ivoryFaint}
                      />
                      {profile.id === currentProfileId ? (
                        <View style={styles.nowBadge}>
                          <Text style={styles.nowBadgeText}>Now</Text>
                        </View>
                      ) : null}
                      <Pressable onPress={() => confirmDeleteProfile(profile)} hitSlop={8}>
                        <Text style={styles.trashGlyph}>🗑</Text>
                      </Pressable>
                      <Text style={styles.profileChevron}>{expanded ? '⌃' : '⌄'}</Text>
                    </Pressable>
                    {expanded ? (
                      <View style={styles.profileBody}>
                        {items.map((item, idx, arr) => renderItemRow(target, item, idx, arr))}
                        <Pressable onPress={() => addRowTo(target)} style={styles.addRowButton}>
                          <Text style={styles.addRowText}>+ Add Row</Text>
                        </Pressable>
                      </View>
                    ) : null}
                  </View>
                );
              })}

              <Pressable onPress={addProfile} style={styles.addProfileButton}>
                <Text style={styles.addProfileText}>+ Add Profile</Text>
              </Pressable>
            </View>
          ) : null}
        </View>
        ) : null}

        {flowView ? (
          <FlowOrbView dayItems={dayItems} weeklyForPeriod={weeklyForPeriod} monthlyForPeriod={monthlyForPeriod} />
        ) : (
        <>
        {/* ─── AGENDA — TODAY (secondary panel; always collapsible — task 2) ── */}
        <View style={styles.panel}>
        <GlassFill />
          <Pressable
            style={styles.panelHeaderSecondary}
            onPress={() => setAgendaOpen((v) => !v)}
          >
            <Text style={styles.panelHeaderTitleSmall}>AGENDA — TODAY</Text>
            <Text style={styles.panelHeaderGlyph}>{agendaOpen ? '⌃' : '⌄'}</Text>
          </Pressable>
          {agendaOpen ? (
            <View ref={agendaTour.onLayoutRef} collapsable={false} style={styles.panelBody}>
              {(() => {
                const ownTitles = new Set(dayItems.map((i) => i.text.trim().toLowerCase()).filter(Boolean));
                const ownTaskIds = new Set(dayItems.map((i) => i.task_id).filter(Boolean) as string[]);
                const ext = externalToday.filter(
                  (e) => !ownTitles.has(e.title.trim().toLowerCase()) && !(e.taskId && ownTaskIds.has(e.taskId)),
                );
                const rows: React.ReactNode[] = [];
                const pending = [...ext];
                const flushBefore = (min: number | null) => {
                  while (pending.length && (min === null || minutesOfDay(pending[0].start) < min)) {
                    const e = pending.shift()!;
                    rows.push(
                      <Pressable
                        key={e.id}
                        style={styles.externalRow}
                        disabled={e.source !== 'task'}
                        onPress={() => promoteTaskToAgenda(e)}
                      >
                        <Text style={styles.externalTime}>{normalizeTime(e.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }))}</Text>
                        <Text style={[styles.externalText, e.completed && styles.itemTextDone]} numberOfLines={2}>{e.title}</Text>
                        <Text style={styles.externalTag}>{e.source === 'task' ? 'KYRS · + AGENDA' : 'KRNOS'}</Text>
                      </Pressable>,
                    );
                  }
                };
                dayItems.forEach((item, idx, arr) => {
                  const m = agendaTimeMinutes(item.time);
                  if (m !== null) flushBefore(m);
                  rows.push(renderItemRow({ scope: 'day' }, item, idx, arr));
                });
                flushBefore(null);
                return rows;
              })()}
              <Pressable onPress={() => addRowTo({ scope: 'day' })} style={styles.addRowButton}>
                <Text style={styles.addRowText}>+ Add Row</Text>
              </Pressable>
            </View>
          ) : null}
        </View>

        {/* ─── WEEKLY — AIMS ───────────────────────────────────────── */}
        <View style={styles.panel}>
        <GlassFill />
          <Pressable style={styles.panelHeaderSecondary} onPress={() => setWeeklyOpen((v) => !v)}>
            <Text style={styles.panelHeaderTitleSmall}>WEEKLY — AIMS</Text>
            <Text style={styles.panelHeaderSub}>
              Week {isoWeekAndYear(currentDate).week} · {isoWeekAndYear(currentDate).year}
            </Text>
            <Text style={styles.panelHeaderGlyph}>{weeklyOpen ? '⌃' : '⌄'}</Text>
          </Pressable>
          {weeklyOpen ? (
            <View style={styles.panelBody}>
              {weeklyForPeriod.length === 0 ? (
                <Text style={styles.emptyInline}>No goals yet for this week.</Text>
              ) : null}
              {weeklyForPeriod.map((goal) => {
                const weeklyRowCount = weeklyForPeriod.length;
                let weeklyDragStep = 0;
                const weeklyDragGesture = Gesture.Pan()
                  .activateAfterLongPress(140)
                  .onStart(() => { weeklyDragStep = 0; })
                  .onUpdate((e) => {
                    const rawStep = Math.round(e.translationY / DRAG_ROW_STEP);
                    const bound = Math.max(0, weeklyRowCount - 1);
                    const step = Math.max(-bound, Math.min(bound, rawStep));
                    while (step > weeklyDragStep) { moveWeeklyGoal(goal.id, 1); weeklyDragStep += 1; }
                    while (step < weeklyDragStep) { moveWeeklyGoal(goal.id, -1); weeklyDragStep -= 1; }
                  });
                const weeklyTapGesture = Gesture.Tap().onEnd(() => setAimLinkPickerFor(goal.id));
                const weeklyCrosshairGesture = Gesture.Exclusive(weeklyDragGesture, weeklyTapGesture);
                return (
                <View
                  key={goal.id}
                  ref={(r) => { weeklyRowRefs.current.set(goal.id, r); }}
                  onLayout={remeasureWeeklyRows}
                  style={[
                    styles.goalRow,
                    highlightedWeeklyGoalId === goal.id && styles.goalRowHighlighted,
                  ]}
                >
                  <GestureDetector gesture={weeklyCrosshairGesture}>
                    <View style={styles.dragHandle} hitSlop={8}>
                      <Crosshair size={17} color={t.ivorySoft} strokeWidth={2} />
                    </View>
                  </GestureDetector>
                  {(goal.linkedGoalIds?.length ?? 0) > 0 ? <Text style={styles.linkGlyph}>⛓</Text> : null}
                  <Pressable
                    style={[styles.checkbox, goal.completed && styles.checkboxDone]}
                    onPress={() => updateWeeklyGoal(goal.id, { completed: !goal.completed })}
                  >
                    {goal.completed ? <Text style={styles.checkboxTick}>✓</Text> : null}
                  </Pressable>
                  <TextInput
                    style={[styles.goalInput, goal.completed && styles.itemTextDone]}
                    value={goal.text}
                    onChangeText={(t) => updateWeeklyGoal(goal.id, { text: t })}
                    placeholder="Goal for this week..."
                    placeholderTextColor={t.ivoryFaint}
                  />
                  <Pressable
                    onPress={() =>
                      Alert.alert('Delete this weekly goal?', 'This permanently removes the goal.', [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Delete', style: 'destructive', onPress: () => deleteWeeklyGoal(goal.id) },
                      ])
                    }
                    hitSlop={8}
                  >
                    <Text style={styles.goalDelete}>×</Text>
                  </Pressable>
                </View>
                );
              })}
              <Pressable onPress={addWeeklyGoal} style={styles.addRowButton}>
                <Text style={styles.addRowText}>+ Add Goal</Text>
              </Pressable>
            </View>
          ) : null}
        </View>

        {/* ─── MONTHLY — GOALS ─────────────────────────────────────── */}
        <View style={styles.panel}>
        <GlassFill />
          <Pressable style={styles.panelHeaderSecondary} onPress={() => setMonthlyOpen((v) => !v)}>
            <Text style={styles.panelHeaderTitleSmall}>MONTHLY — GOALS</Text>
            <Text style={styles.panelHeaderSub}>{formatMonthFull(currentDate)}</Text>
            <Text style={styles.panelHeaderGlyph}>{monthlyOpen ? '⌃' : '⌄'}</Text>
          </Pressable>
          {monthlyOpen ? (
            <View style={styles.panelBody}>
              {monthlyForPeriod.length === 0 ? (
                <Text style={styles.emptyInline}>No goals yet for {formatMonthFull(currentDate)}.</Text>
              ) : null}
              {monthlyForPeriod.map((goal) => {
                const monthlyRowCount = monthlyForPeriod.length;
                let monthlyDragStep = 0;
                const monthlyDragGesture = Gesture.Pan()
                  .activateAfterLongPress(140)
                  .onStart(() => { monthlyDragStep = 0; })
                  .onUpdate((e) => {
                    const rawStep = Math.round(e.translationY / DRAG_ROW_STEP);
                    const bound = Math.max(0, monthlyRowCount - 1);
                    const step = Math.max(-bound, Math.min(bound, rawStep));
                    while (step > monthlyDragStep) { moveMonthlyGoal(goal.id, 1); monthlyDragStep += 1; }
                    while (step < monthlyDragStep) { moveMonthlyGoal(goal.id, -1); monthlyDragStep -= 1; }
                  });
                return (
                <View key={goal.id} style={styles.goalRow}>
                  <GestureDetector gesture={monthlyDragGesture}>
                    <View style={styles.dragHandle} hitSlop={8}>
                      <Crosshair size={17} color={t.ivorySoft} strokeWidth={2} />
                    </View>
                  </GestureDetector>
                  <Pressable
                    style={[styles.checkbox, goal.completed && styles.checkboxDone]}
                    onPress={() => updateMonthlyGoal(goal.id, { completed: !goal.completed })}
                  >
                    {goal.completed ? <Text style={styles.checkboxTick}>✓</Text> : null}
                  </Pressable>
                  <TextInput
                    style={[styles.goalInput, goal.completed && styles.itemTextDone]}
                    value={goal.text}
                    onChangeText={(t) => updateMonthlyGoal(goal.id, { text: t })}
                    placeholder="Goal for this month..."
                    placeholderTextColor={t.ivoryFaint}
                  />
                  <Pressable
                    onPress={() =>
                      Alert.alert('Delete this monthly goal?', 'This permanently removes the goal.', [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Delete', style: 'destructive', onPress: () => deleteMonthlyGoal(goal.id) },
                      ])
                    }
                    hitSlop={8}
                  >
                    <Text style={styles.goalDelete}>×</Text>
                  </Pressable>
                </View>
                );
              })}
              <Pressable onPress={addMonthlyGoal} style={styles.addRowButton}>
                <Text style={styles.addRowText}>+ Add Goal</Text>
              </Pressable>
            </View>
          ) : null}
        </View>
        </>
        )}
      </ScrollView>

      {/* ── Trajectory view (ledger #2) — opened from an item's Info tab ── */}
      <TrajectoryView
        visible={!!trajectoryTarget}
        onClose={() => setTrajectoryTarget(null)}
        taskText={trajectoryTarget?.text ?? ''}
        listLabel={trajectoryTarget?.listLabel ?? null}
      />

      {/* ── Manual reschedule (blue clock, AGENDA — TODAY status expand) ── */}
      <Modal
        visible={!!rescheduleTarget}
        transparent
        animationType="fade"
        onRequestClose={closeManualReschedule}
      >
        <Pressable style={styles.pickerBackdrop} onPress={closeManualReschedule}>
          <Pressable style={styles.rescheduleModalBox} onPress={() => {}}>
            <GlassFill />
            <Text style={styles.rescheduleTitle} numberOfLines={2}>
              Reschedule "{rescheduleTarget?.text || 'Untitled task'}"
            </Text>

            <Text style={styles.rescheduleLabel}>Date (yyyy-MM-dd)</Text>
            <TextInput
              style={styles.rescheduleInput}
              value={rescheduleDateInput}
              onChangeText={setRescheduleDateInput}
              placeholder="yyyy-MM-dd"
              placeholderTextColor={t.ivoryFaint}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <View style={styles.rescheduleChipRow}>
              <Pressable style={styles.rescheduleChip} onPress={() => setRescheduleDateInput(dateKey)}>
                <Text style={styles.rescheduleChipText}>Today</Text>
              </Pressable>
              <Pressable
                style={styles.rescheduleChip}
                onPress={() => setRescheduleDateInput(dateKeyOf(addDaysLocal(currentDate, 1)))}
              >
                <Text style={styles.rescheduleChipText}>Tomorrow</Text>
              </Pressable>
            </View>

            <Text style={styles.rescheduleLabel}>Time (HH:mm)</Text>
            <TextInput
              style={styles.rescheduleInput}
              value={rescheduleTimeInput}
              onChangeText={setRescheduleTimeInput}
              placeholder="HH:mm"
              placeholderTextColor={t.ivoryFaint}
              autoCapitalize="none"
              autoCorrect={false}
            />

            {rescheduleError ? <Text style={styles.error}>{rescheduleError}</Text> : null}

            <View style={styles.rescheduleButtonRow}>
              <Pressable style={styles.rescheduleCancelButton} onPress={closeManualReschedule}>
                <Text style={styles.rescheduleCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.rescheduleSaveButton, rescheduleSaving && { opacity: 0.6 }]}
                onPress={() => void saveManualReschedule()}
                disabled={rescheduleSaving}
              >
                <Text style={styles.rescheduleSaveText}>{rescheduleSaving ? 'Saving…' : 'Save'}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Time segments modal ── */}
      <Modal visible={timeSegmentsOpen} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setTimeSegmentsOpen(false)}>
        <Atmos style={styles.modalRoot}>
          <View style={styles.modalHeaderBar}>
            <Text style={styles.modalHeaderTitle}>Profile Time Segments</Text>
            <Pressable onPress={() => setTimeSegmentsOpen(false)}>
              <Text style={styles.modalHeaderAction}>Done</Text>
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={styles.modalScrollContent}>
            <Text style={styles.segmentsHint}>
              Set a start and end time (HH:MM, 24h) for each profile. Whichever profile contains the current time auto-expands.
            </Text>
            {profiles.length === 0 ? (
              <Text style={styles.emptyInline}>No profiles yet. Add one first.</Text>
            ) : (
              profiles.map((p) => (
                <View key={p.id} style={styles.segmentRow}>
                  <Text style={styles.segmentLabel} numberOfLines={1}>{p.label || 'Untitled'}</Text>
                  <TextInput
                    style={styles.segmentInput}
                    value={p.startTime ?? ''}
                    onChangeText={(t) => updateProfileTimes(p.id, t || null, p.endTime ?? null)}
                    placeholder="HH:MM"
                    placeholderTextColor={t.ivoryFaint}
                  />
                  <Text style={styles.segmentArrow}>→</Text>
                  <TextInput
                    style={styles.segmentInput}
                    value={p.endTime ?? ''}
                    onChangeText={(t) => updateProfileTimes(p.id, p.startTime ?? null, t || null)}
                    placeholder="HH:MM"
                    placeholderTextColor={t.ivoryFaint}
                  />
                </View>
              ))
            )}
          </ScrollView>
        </Atmos>
      </Modal>

      {/* Tap the ⛓ handle on a WEEKLY aim → link/unlink it against MONTHLY goals. */}
      <Modal
        visible={aimLinkPickerFor !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setAimLinkPickerFor(null)}
      >
        <Pressable style={styles.pickerBackdrop} onPress={() => setAimLinkPickerFor(null)}>
          <Pressable style={styles.pickerCard} onPress={() => {}}>
            <GlassFill />
            <Text style={styles.pickerHeader}>Link to monthly goal</Text>
            {monthlyForPeriod.length === 0 ? (
              <Text style={[styles.tabHint, { paddingHorizontal: 18, paddingBottom: 14 }]}>
                No monthly goals yet — add one in MONTHLY — GOALS.
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 280 }}>
                {monthlyForPeriod.map((monthlyGoal) => {
                  const openAim = weeklyForPeriod.find((g) => g.id === aimLinkPickerFor);
                  const linked = (openAim?.linkedGoalIds ?? []).includes(monthlyGoal.id);
                  return (
                    <Pressable
                      key={monthlyGoal.id}
                      style={styles.linkPickerRow}
                      onPress={() => {
                        if (aimLinkPickerFor) toggleWeeklyGoalLink(aimLinkPickerFor, monthlyGoal.id);
                      }}
                    >
                      <View style={[styles.linkPickerCheckbox, linked && styles.linkPickerCheckboxOn]}>
                        {linked ? <Text style={styles.linkPickerCheckMark}>✓</Text> : null}
                      </View>
                      <Text style={styles.linkPickerRowText} numberOfLines={2}>
                        {monthlyGoal.text || 'Untitled goal'}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            )}
            <Pressable style={styles.linkPickerDoneButton} onPress={() => setAimLinkPickerFor(null)}>
              <Text style={styles.linkPickerDoneText}>Done</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  glassHighlight: { position: 'absolute', top: 0, left: 12, right: 12, height: 1, backgroundColor: t.panelHighlight },
  content: { padding: 12, paddingBottom: 32, gap: 12 },
  helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 8 },
  error: { color: c.danger, fontSize: 13, marginBottom: 4 },
  emptyTitle: { fontSize: 16, fontWeight: '800', color: c.text, textAlign: 'center', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  emptyInline: { fontSize: 13, color: c.slate, fontStyle: 'italic', textAlign: 'center', paddingVertical: 10 },

  // ── Panels ──
  panel: {
    borderRadius: radii.panel,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent', borderBottomWidth: 1, borderBottomColor: t.navyLine,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  panelHeaderSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent', borderBottomWidth: 1, borderBottomColor: t.navyLine,
    paddingHorizontal: 14,
    paddingVertical: 9,
    gap: 8,
  },
  panelHeaderTitle: { fontSize: 18, fontWeight: '900', color: t.goldBright, letterSpacing: 2.5, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  panelHeaderTitleSmall: { fontSize: 14, fontWeight: '900', color: t.goldBright, letterSpacing: 2, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  panelHeaderSub: { flex: 1, fontSize: 11, color: t.ivoryFaint, textAlign: 'right', marginRight: 8 },
  panelHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  panelHeaderGlyph: { color: t.ivory, fontSize: 16, fontWeight: '700' },
  setsLabel: { color: t.ivorySoft, fontSize: 12, fontWeight: '700' },
  panelBody: { backgroundColor: 'transparent', padding: 10, gap: 10 },

  dateNavRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16, paddingBottom: 4 },
  dateNavArrow: { fontSize: 22, fontWeight: '900', color: c.blueText, paddingHorizontal: 8 },
  dateNavLabel: { fontSize: 15, fontWeight: '700', color: c.text, minWidth: 160, textAlign: 'center' },

  // ── Profiles ──
  profileCard: {
    borderRadius: radii.tile,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent', borderBottomWidth: 1, borderBottomColor: t.navyLine,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  profileLabelInput: { flex: 1, color: t.ivory, fontWeight: '800', fontSize: 15 },
  nowBadge: { backgroundColor: t.gold, borderRadius: radii.pill, paddingHorizontal: 8, paddingVertical: 2 },
  nowBadgeText: { fontFamily: DISPLAY_FONT, fontSize: 9, fontWeight: '900', color: t.onGold, textTransform: 'uppercase', letterSpacing: 0.5 },
  trashGlyph: { fontSize: 14 },
  profileChevron: { color: t.ivorySoft, fontSize: 14, fontWeight: '700' },
  profileBody: { backgroundColor: 'transparent', padding: 8, gap: 8 },

  addRowButton: { alignSelf: 'center', backgroundColor: t.glass, borderRadius: radii.tile, paddingHorizontal: 14, paddingVertical: 7, marginTop: 4, ...neu.raisedSm },
  addRowText: { fontSize: 12, fontWeight: '700', color: c.blueText },
  addProfileButton: { alignSelf: 'center', backgroundColor: c.primary, borderRadius: radii.tile, paddingHorizontal: 16, paddingVertical: 9, marginTop: 4, ...neu.glowPrimary },
  addProfileText: { fontSize: 13, fontWeight: '800', color: t.onGold },

  // ── Item row ──
  itemCard: {
    backgroundColor: 'transparent',
    borderRadius: radii.tile,
    padding: 8,
    gap: 6,
    borderWidth: 1, borderColor: t.navyLine, overflow: 'hidden', boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  itemCardDragging: {
    borderColor: t.gold,
    boxShadow: `0 0 14px ${t.goldGlow}`,
  },
  itemCardCurrent: {
    borderColor: t.gold,
    boxShadow: `0 0 14px ${t.goldGlow}`,
  },
  externalRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 10,
    borderRadius: radii.tile, borderWidth: 1, borderColor: t.navyLine, opacity: 0.85,
  },
  externalTime: { fontSize: 12, fontWeight: '700', color: c.slate, minWidth: 64 },
  externalText: { flex: 1, fontSize: 14, color: c.text },
  externalTag: { fontSize: 10, fontWeight: '800', color: c.slate, letterSpacing: 0.6 },
  itemMainRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  itemStatusRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 34 },
  dragHandle: { alignItems: 'center', justifyContent: 'center', paddingHorizontal: 2, paddingVertical: 4 },
  // Task 4: shown next to the drag handle when this task is linked to a
  // WEEKLY aim (via drag-to-link).
  linkGlyph: { fontSize: 11, color: c.primary, marginRight: -2 },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: c.slate,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxDone: { backgroundColor: c.lime, borderColor: c.blue },
  checkboxTick: { fontSize: 13, fontWeight: '900', color: c.blueText },
  itemTextInput: {
    flex: 2,
    fontSize: 15,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    ...neu.insetDeep,
  },
  itemTextDone: { textDecorationLine: 'line-through', color: t.ivoryFaint },
  menuGlyph: { fontSize: 18, color: c.slate, fontWeight: '900', paddingHorizontal: 4 },

  timeInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    ...neu.insetDeep,
  },
  durationInput: {
    width: 56,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 6,
    paddingVertical: 6,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    ...neu.insetDeep,
  },
  timerToggle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  timerToggleRunning: { backgroundColor: c.primary, ...neu.glowPrimary },
  timerToggleGlyph: { fontSize: 13, color: c.text },
  durationRemaining: { fontSize: 12, fontWeight: '800', color: c.blueText, fontVariant: ['tabular-nums'] },
  durationWarn: { color: c.danger },
  durationDone: { color: t.onPace },
  resetLink: { fontSize: 11, color: c.slate, textDecorationLine: 'underline' },
  pomodoroPhaseLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 10,
    fontWeight: '900',
    color: c.blueText,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: c.lime,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  pomodoroPhaseLabelBreak: { color: t.onPace, backgroundColor: c.limeSoft },

  // ── Nail timer rows ──
  nailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginLeft: 20,
    borderWidth: 1, borderColor: t.navyLine,
  },
  nailToggle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  nailToggleRunning: { backgroundColor: c.primary, ...neu.glowPrimary },
  nailToggleGlyph: { fontSize: 11, color: c.text },
  nailTextInput: {
    flex: 1,
    fontSize: 13,
    fontStyle: 'italic',
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    ...neu.insetDeep,
  },
  nailRemaining: { fontSize: 12, fontWeight: '700', color: c.blueText, fontVariant: ['tabular-nums'] },
  nailRemainingWarn: { color: c.danger },
  nailRemove: { fontSize: 16, color: c.slate, paddingHorizontal: 4 },

  // ── Missed reason ──
  missedBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    backgroundColor: t.glass,
    borderRadius: 14,
    padding: 6,
    borderWidth: 1, borderColor: t.navyLine,
  },
  missedInput: {
    flex: 1,
    fontSize: 13,
    color: c.text,
    minHeight: 44,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 6,
    ...neu.insetDeep,
  },
  missedClear: { fontSize: 16, color: c.slate, paddingHorizontal: 4 },

  // ── Goals (monthly/weekly) ──
  goalRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  goalInput: {
    flex: 1,
    fontSize: 14,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    ...neu.insetDeep,
  },
  goalDelete: { fontSize: 18, color: c.slate, paddingHorizontal: 4 },
  // Tap-to-link (weekly aim → monthly goal): small circular ⛓ button on each
  // Task 4: WEEKLY aim row highlight while a task is being drag-hovered over it.
  goalRowHighlighted: {
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderWidth: 1, borderColor: t.navyLine,
  },

  // ── FLOW toggle + orb view (task 5) ──
  flowToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 4,
  },
  flowToggleLabel: { fontSize: 12, fontWeight: '800', color: c.blueText, letterSpacing: 0.5 },
  flowCanvasOuter: { alignItems: 'center', backgroundColor: 'transparent', padding: 8 },
  flowCanvas: { position: 'relative' },
  flowOrbWrap: { position: 'absolute', alignItems: 'center' },
  flowOrb: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  flowOrbAim: { backgroundColor: c.lime },
  flowOrbDone: { backgroundColor: c.primary, ...neu.glowPrimary },
  flowOrbGlyph: { fontSize: 10, fontWeight: '900', color: c.blueText },
  flowOrbGlyphDone: { color: t.onGold },
  flowOrbLabel: { fontSize: 10, color: c.slate, textAlign: 'center', marginTop: 4 },

  // ── Modals ──
  modalRoot: { flex: 1, backgroundColor: t.night },
  modalHeaderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: c.glassRim,
  },
  modalHeaderTitle: { fontSize: 16, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  modalHeaderAction: { fontSize: 14, fontWeight: '800', color: c.blueText },
  modalScrollContent: { padding: 16, gap: 10, paddingBottom: 40 },

  noteTextArea: {
    minHeight: 140,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    padding: 10,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  clearNoteButton: { alignSelf: 'flex-end', paddingVertical: 6, paddingHorizontal: 10 },
  clearNoteText: { color: c.danger, fontWeight: '700', fontSize: 13 },

  templateRow: { flexGrow: 0, marginBottom: 4 },
  templateChip: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderWidth: 1, borderColor: t.navyLine,
  },
  templateChipActive: { backgroundColor: c.lime, borderWidth: 1, borderColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  templateChipText: { fontSize: 12, fontWeight: '700', color: c.text },
  templateChipTextActive: { color: c.blueText },

  browseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1, borderColor: t.navyLine,
  },
  browseArrow: { fontSize: 20, fontWeight: '900', color: c.blueText, paddingHorizontal: 6 },
  browseArrowDisabled: { color: c.glassRim },
  browseLabel: { flex: 1, fontSize: 11, color: c.slate, textAlign: 'center' },

  designGroup: { gap: 4, marginBottom: 8 },
  designCategoryLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '900',
    color: c.blueText,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: c.lime,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
  },
  designRow: { flexDirection: 'row', gap: 8, paddingLeft: 4 },
  designRowLabel: { width: 92, fontSize: 12, color: c.slate, paddingTop: 4 },
  designRowValue: {
    flex: 1,
    fontSize: 13,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    padding: 6,
    minHeight: 32,
    ...neu.insetDeep,
  },
  restoreButton: { alignSelf: 'center', backgroundColor: c.lime, borderRadius: radii.tile, paddingHorizontal: 14, paddingVertical: 8, marginTop: 6, ...neu.inset },
  restoreButtonText: { fontSize: 12, fontWeight: '800', color: c.blueText },

  segmentsHint: { fontSize: 12, color: c.slate, marginBottom: 6 },
  segmentRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  segmentLabel: { flex: 1, fontSize: 13, fontWeight: '700', color: c.text },
  segmentInput: {
    width: 76,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    ...neu.insetDeep,
  },
  segmentArrow: { fontSize: 13, color: c.slate },

  // ── Redesigned item row: status checkbox · title · time + accordion tabs ──
  // Tap = toggle Completed. Long-press = pick a Missed reason.
  statusCheckbox: {
    width: 26,
    height: 26,
    borderRadius: 6,
    backgroundColor: t.glass,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  statusCheckboxDone: { backgroundColor: t.onPace, ...neu.glowPrimary },
  statusCheckboxMissed: { backgroundColor: c.lime, borderWidth: 1, borderColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  statusCheckboxMark: { color: t.onGold, fontSize: 15, fontWeight: '900' },
  statusCheckboxMissedMark: { color: c.danger, fontSize: 15, fontWeight: '900' },

  // ── Long-press status expand (Done/Missed/Auto-rescued/Manual reschedule) ──
  statusControlWrap: { flexDirection: 'row', alignItems: 'center' },
  statusExpandRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 6, overflow: 'hidden' },
  statusOptionBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  statusOptionDone: { backgroundColor: t.onPace },
  statusOptionMissed: { backgroundColor: c.danger },
  statusOptionAuto: { backgroundColor: t.behind },
  statusOptionManual: { backgroundColor: c.blue },

  accordionToggle: {
    width: 30,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  accordionToggleOpen: { backgroundColor: c.lime, borderWidth: 1, borderColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },

  // New ORDER sheet fields (stacked label + prompt input, clear contrast)
  orderField: { marginBottom: 12 },
  orderFieldLabel: {
    fontSize: 13,
    fontWeight: '900',
    color: c.blueText,
    marginBottom: 5,
    letterSpacing: 0.3,
  },
  orderFieldInput: {
    minHeight: 40,
    fontSize: 14,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 8,
    ...neu.insetDeep,
  },

  timeInputNarrow: {
    width: 62,
    height: 34,
    borderRadius: radii.tile,
    backgroundColor: c.inputFill,
    paddingHorizontal: 8,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    ...neu.insetDeep,
  },

  tabsWrap: { marginTop: 10, paddingTop: 10 },
  tabBar: { flexDirection: 'row', gap: 8, paddingRight: 8 },
  tabChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  tabChipActive: { backgroundColor: c.lime, borderWidth: 1, borderColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  tabChipText: { fontSize: 12, fontWeight: '700', color: c.slate },
  tabChipTextActive: { color: c.blueText },
  tabDeleteChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  tabDeleteText: { fontSize: 12, fontWeight: '700', color: c.danger },

  tabPanel: {
    marginTop: 10,
    padding: 10,
    borderRadius: radii.tile,
    backgroundColor: 'transparent',
    borderWidth: 1, borderColor: t.navyLine, overflow: 'hidden', boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  tabHint: { fontSize: 12, color: c.slate, fontStyle: 'italic', paddingVertical: 6 },
  tabAddButton: {
    marginTop: 8,
    paddingVertical: 10,
    borderRadius: radii.tile,
    backgroundColor: c.lime,
    alignItems: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  tabAddButtonText: { fontSize: 13, fontWeight: '700', color: c.blueText },
  tabNoteArea: {
    minHeight: 90,
    borderRadius: radii.tile,
    backgroundColor: c.inputFill,
    padding: 10,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  durationTab: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  durationTabInput: {
    width: 70,
    height: 36,
    borderRadius: radii.tile,
    backgroundColor: c.inputFill,
    paddingHorizontal: 10,
    fontSize: 14,
    color: c.text,
    textAlign: 'center',
    ...neu.insetDeep,
  },
  infoLine: { fontSize: 13, color: c.slate, paddingVertical: 3 },
  infoValue: { color: c.text, fontWeight: '700' },

  // THEMIS tab
  orderBackRow: { paddingVertical: 6, marginBottom: 4 },
  orderBackText: { fontSize: 13, fontWeight: '700', color: c.blueText },
  newOrderButton: {
    paddingVertical: 11,
    borderRadius: radii.tile,
    backgroundColor: c.lime,
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1, borderColor: t.navyLine,
  },
  newOrderButtonText: { fontSize: 14, fontWeight: '800', color: c.blueText },
  protoGroupRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 9,
  },
  protoGroupChevron: { fontSize: 12, color: c.slate, width: 14 },
  protoGroupName: { flex: 1, fontSize: 14, fontWeight: '800', color: c.text },
  protoGroupCount: { fontSize: 12, color: c.slate, fontWeight: '700' },
  protoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 9,
    paddingLeft: 22,
    paddingRight: 8,
  },
  protoItemSelected: { backgroundColor: c.lime, borderRadius: radii.tile, ...neu.inset },
  protoItemText: { flex: 1, fontSize: 14, fontWeight: '600', color: c.text },
  protoItemTextSelected: { color: c.blueText, fontWeight: '800' },
  protoItemCheck: { fontSize: 14, fontWeight: '900', color: c.blueText },
  applyButton: {
    marginTop: 12,
    paddingVertical: 12,
    borderRadius: radii.tile,
    backgroundColor: c.primary,
    alignItems: 'center',
    ...neu.glowPrimary,
  },
  applyButtonText: { fontSize: 14, fontWeight: '800', color: t.onGold },

  // Shared picker (status / miss reason)
  pickerBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 32 },
  pickerCard: {
    width: '100%',
    maxWidth: 320,
    borderRadius: radii.panel,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    paddingVertical: 4,
    borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  pickerHeader: { fontFamily: DISPLAY_FONT,
    fontSize: 12,
    fontWeight: '800',
    color: c.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 18,
    paddingVertical: 10,
  },
  pickerOption: { paddingHorizontal: 18, paddingVertical: 14 },
  pickerOptionText: { fontSize: 15, fontWeight: '700', color: c.text },
  pickerOptionMuted: { fontSize: 14, fontWeight: '600', color: c.slate },
  // ── Tap-to-link weekly-aim picker (task 4b) ──
  linkPickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  linkPickerCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    backgroundColor: t.glass,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  linkPickerCheckboxOn: {
    backgroundColor: c.primary,
    boxShadow: `0 6px 18px ${t.goldGlow}`,
  },
  linkPickerCheckMark: { fontSize: 13, fontWeight: '800', color: t.onGold },
  linkPickerRowText: { flex: 1, fontSize: 14, fontWeight: '600', color: c.text },
  linkPickerDoneButton: {
    marginHorizontal: 18,
    marginTop: 8,
    marginBottom: 14,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: c.primary,
    boxShadow: `0 6px 18px ${t.goldGlow}`,
  },
  linkPickerDoneText: { fontSize: 14, fontWeight: '800', color: t.onGold },

  // ── Manual reschedule (blue clock) modal — mirrors the Clio/Kyrs
  // reschedule-dialog layout, replicated locally with this file's tokens. ──
  rescheduleModalBox: {
    width: '100%',
    maxWidth: 340,
    backgroundColor: 'transparent',
    borderRadius: radii.panel,
    padding: 16,
    borderWidth: 1, borderColor: t.navyLine, overflow: 'hidden', boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  rescheduleTitle: { fontSize: 15, fontWeight: '800', color: c.text, marginBottom: 14, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  rescheduleLabel: { fontFamily: DISPLAY_FONT, fontSize: 12, fontWeight: '700', color: c.slate, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 10 },
  rescheduleInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 15,
    color: c.text,
    ...neu.insetDeep,
  },
  rescheduleChipRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  rescheduleChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  rescheduleChipText: { fontSize: 12, fontWeight: '700', color: c.text },
  rescheduleButtonRow: { flexDirection: 'row', gap: 10, marginTop: 18 },
  rescheduleCancelButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    borderWidth: 1, borderColor: t.navyLine,
  },
  rescheduleCancelText: { fontSize: 14, fontWeight: '700', color: c.text },
  rescheduleSaveButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: radii.tile,
    backgroundColor: c.primary,
    ...neu.glowPrimary,
  },
  rescheduleSaveText: { fontSize: 14, fontWeight: '800', color: t.onGold },
}),
);
